import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SkeletonLoader from 'components/ui/SkeletonLoader';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  X, Briefcase, Check, Plus, Minus, AlertCircle, ChevronRight,
  ChevronLeft, Building2, MapPin, GraduationCap, Users,
  Bell, Sparkles, Search, Trash2, GripVertical,
  ChevronDown, AlertTriangle, Clock, XCircle
} from 'lucide-react';
import { DesignationArray, SkillsListArray } from 'types/onboarding';
import { apiService } from 'services/apiService';
import { toast } from 'react-toastify';
import { useDebounce } from 'hooks/useDebounce';
import Select from 'react-select';
import GooglePlacesAutocomplete from 'components/common/GooglePlacesAutocomplete';
import 'assets/css/AddJobModal.css';
import 'assets/css/UserExperience.css';
import FallbackPlacesAutocomplete from 'components/common/FallbackPlacesAutocomplete';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import VerificationRequiredModal from '../../ConfirmationModals/VerificationRequiredModal/VerificationRequiredModal';
import CreditConfirmPopup from 'components/payments/CreditConfirmPopup';
import { useCreditConfirm } from 'customhooks/useCreditConfirm';
import { notifyCreditChange } from 'utils/creditEvents';

// ─── Types & Interfaces ──────────────────────────────────────────────────────

export interface NewJobPayload {
  designation_id: number;
  job_title: string;
  skills: number[];
  employment_type_ids: number[];
  work_experience_years: number;
  work_experience_months: number;
  experience_id: number | string;
  description: string;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string;
  salary_period: string;
  salary_visibility?: 'range' | 'not_disclosed' | 'negotiable' | 'competitive';
  openings_count: number | null;
  location_text: string;
  location_lat: number | null;
  location_lng: number | null;
  industry_tags: number[] | string[]; // Can be array of IDs or names based on API expectations
  responsibilities: string[];
  requirements: string[];
  benefits: string[] | null;
  id?: string | null;
  is_closed?: boolean;
  department?: string;
  qualification?: string[];
  gender_preference?: string;
  candidate_industries?: number[];
  screening_questions?: ScreeningQuestion[];
  notify_email?: boolean;
  notify_sms?: boolean;
  notify_whatsapp?: boolean;
  work_mode?: 'remote' | 'hybrid' | 'onsite' | '';
  application_deadline?: string | null;
  language_requirements?: string[];
  willingness_to_relocate?: 'yes' | 'no' | 'negotiable';
  age_min?: number | null;
  age_max?: number | null;
  auto_reject_rules?: string[];
  about_company?: string;
  perks?: string[];
  careers_page_link?: string;
  notify_emails?: string[];
  ack_template?: string;
  location?: string;
}

export interface ScreeningQuestion {
  id?: string;
  text: string;
  options?: string[];
  mandatory?: boolean;
  type?: 'text' | 'single_choice' | 'multiple_choice' | 'yes_no';
  enabled?: boolean;
}

interface AddJobModalProps {
  onCancel: () => void;
  jobDetails: any;
  companyBenefits?: { title: string; description: string; created_at?: string }[];
  handleAddJob: (data: NewJobPayload) => Promise<any> | any;
  onAutoDraft?: (data: NewJobPayload) => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const STEPS = [
  { id: 'basics', label: 'Job Basics', icon: Briefcase, description: 'Title, salary & essentials' },
  { id: 'preferences', label: 'Candidate Preferences', icon: Users, description: 'Skills & requirements' },
  { id: 'description', label: 'Job Description', icon: Building2, description: 'Role details & benefits' },
  { id: 'communication', label: 'Job Posting Preview', icon: Bell, description: ' Job Preview ' },
] as const;
const STEP_API_MAP: Record<string, string> = {
  basics: 'basics',
  preferences: 'preferences',
  description: 'description',
  communication: 'communication', // important
};
const getApiStep = (step: string) => STEP_API_MAP[step] || step;
type StepIndex = 0 | 1 | 2 | 3;

const normalizeDateOnly = (value: any): string => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';

    const isoDateMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDateMatch) {
      return isoDateMatch[1];
    }
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: 1, label: 'Full-Time', icon: 'clock' },
  { value: 2, label: 'Part-Time', icon: 'clock-4' },
  { value: 3, label: 'Contract', icon: 'file-text' },
  { value: 4, label: 'Temporary', icon: 'clock-3' },
  { value: 5, label: 'Internship', icon: 'graduation-cap' },
  { value: 6, label: 'Freelance', icon: 'laptop' },
  { value: 7, label: 'Seasonal', icon: 'calendar' },
];

const SALARY_CURRENCY_OPTIONS = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'];
const SALARY_PERIOD_OPTIONS = [
  { value: 'year', label: 'Per Year' },
  { value: 'month', label: 'Per Month' },
  { value: 'hour', label: 'Per Hour' },
];

const getMaxSalaryForPeriod = (period: string) => {
  const p = (period || '').toLowerCase();
  if (p === 'hour') {
    return { max: 10000, label: '10,000', shortLabel: '10,000', maxDigits: 5 };
  }
  if (p === 'month') {
    return { max: 10000000, label: '1 Crore (≤ 1,00,00,000)', shortLabel: '1,00,00,000', maxDigits: 8 };
  }
  // Default: year (12 Crore)
  return { max: 120000000, label: '12 Crore (≤ 12,00,00,000)', shortLabel: '12,00,00,000', maxDigits: 9 };
};

const QUALIFICATION_OPTIONS = [
  { value: '12th Pass', label: '12th Pass', level: 1 },
  { value: 'Diploma', label: 'Diploma', level: 2 },
  { value: 'Graduate', label: 'Graduate', level: 3 },
  { value: 'Post-Graduate', label: 'Post-Graduate', level: 4 },
  { value: 'Doctorate', label: 'Doctorate', level: 5 },
];

const GENDER_OPTIONS = [
  { value: 'Any', label: 'Any' },
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

const PERK_SUGGESTIONS = [
  'Health Insurance', 'Flexible Hours', 'Work From Home',
  'Annual Bonus', 'Learning Budget', 'Gym Membership',
  'Free Meals', 'Transport Allowance', 'Stock Options',
  'Paid Parental Leave', 'Mental Health Days', 'Conference Budget'
];

const DEPT_OPTIONS = [
  'Engineering', 'Product', 'Design', 'Marketing', 'Sales',
  'Customer Success', 'Operations', 'HR', 'Finance', 'Legal', 'IT Support'
];

const SCREENING_TEMPLATES: Array<Omit<ScreeningQuestion, 'id'>> = [
  {
    text: 'What is your current CTC?',
    type: 'text',
    // placeholder: 'e.g. 8 LPA'
  },
  {
    text: 'What is your expected CTC?',
    type: 'text',
    // placeholder: 'e.g. 12 LPA'
  },
  {
    text: 'What is your notice period?',
    type: 'single_choice',
    options: ['Immediate', '15 Days', '1 Month', '2 Months', '3 Months+']
  },
  {
    text: 'Are you comfortable with in-person interviews?',
    type: 'yes_no'
  },
  {
    text: 'Do you have experience with Agile methodologies?',
    type: 'yes_no'
  }
];

type SalaryVisibilityValue = 'range'
  | 'not_disclosed' | 'negotiable' | 'competitive';
type WorkModeValue = 'remote' | 'hybrid' | 'onsite' | '';
type RelocateValue = 'yes' | 'no' | 'negotiable';

interface JobFormOptionsApiData {
  employment_types?: Array<{ id: number; name: string }>;
  work_modes?: Array<{ id: number; name: string }>;
  salary_currencies?: Array<{ id: number; code: string; name: string; symbol?: string; icon_url?: string }>;
  salary_periods?: Array<{ value: string; label: string }>;
  salary_visibility_options?: Array<{ value: SalaryVisibilityValue; label: string }>;
  qualifications?: Array<{ value: string; label: string; level?: number }>;
  gender_preferences?: Array<{ value: string; label: string }>;
  departments?: Array<{ value: string; label: string }>;
  benefit_suggestions?: Array<{ benefit_key?: string; name: string; icon_url?: string }>;
  willingness_to_relocate_options?: Array<{ value: RelocateValue; label: string }>;
  screening_templates?: Array<Omit<ScreeningQuestion, 'id'>>;
}

interface SalaryCurrencyOption {
  value: string;
  label: string;
  symbol?: string;
  iconUrl?: string;
}

const FALLBACK_WORK_MODE_OPTIONS: Array<{ value: WorkModeValue; label: string }> = [
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'onsite', label: 'On-site' },
];

const FALLBACK_RELOCATE_OPTIONS: Array<{ value: RelocateValue; label: string }> = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'negotiable', label: 'Negotiable' },
];

const FALLBACK_SALARY_VISIBILITY_OPTIONS: Array<{ value: SalaryVisibilityValue; label: string }> = [
  // { value: 'range', label: 'Save Range' },
  // { value: 'not_disclosed', label: 'Not Disclosed' },
  // { value: 'negotiable', label: 'Negotiable' },
  // { value: 'competitive', label: 'Competitive' },
];

function normalizeWorkModeValue(value: string): WorkModeValue {
  const normalized = value.toLowerCase().replace(/[\s_-]+/g, '');
  if (normalized === 'onsite') return 'onsite';
  if (normalized === 'remote') return 'remote';
  if (normalized === 'hybrid') return 'hybrid';
  return '';
}

function withQuestionIds(questions: Array<Omit<ScreeningQuestion, 'id'>>): ScreeningQuestion[] {
  return questions.map((q) => ({
    ...q,
    id: generateId(),
    enabled: q.enabled ?? true,
    mandatory: q.mandatory ?? false,
    type: q.type ?? 'text',
    options: q.options ?? [],
  }));
}

function buildJobFormOptions(data?: JobFormOptionsApiData) {
  const workModeOptions = data?.work_modes
    ?.map((mode) => ({ value: normalizeWorkModeValue(mode.name), label: mode.name }))
    .filter((mode): mode is { value: Exclude<WorkModeValue, ''>; label: string } => Boolean(mode.value));

  return {
    employmentTypeOptions:
      data?.employment_types?.map((type) => ({ value: type.id, label: type.name, icon: 'clock' })) ||
      EMPLOYMENT_TYPE_OPTIONS,
    workModeOptions: workModeOptions?.length ? workModeOptions : FALLBACK_WORK_MODE_OPTIONS,
    salaryCurrencyOptions:
      data?.salary_currencies?.map((currency) => ({
        value: currency.code,
        label: currency.symbol ? `${currency.code} (${currency.symbol})` : currency.code,
        symbol: currency.symbol,
        iconUrl: currency.icon_url,
      })) || SALARY_CURRENCY_OPTIONS.map((code) => ({ value: code, label: code })),
    salaryPeriodOptions: data?.salary_periods || SALARY_PERIOD_OPTIONS,
    salaryVisibilityOptions: data?.salary_visibility_options || FALLBACK_SALARY_VISIBILITY_OPTIONS,
    qualificationOptions: data?.qualifications || QUALIFICATION_OPTIONS,
    genderOptions: data?.gender_preferences || GENDER_OPTIONS,
    departmentOptions: data?.departments?.map((department) => department.value || department.label) || DEPT_OPTIONS,
    perkSuggestions: data?.benefit_suggestions?.map((benefit) => benefit.name).filter(Boolean) || PERK_SUGGESTIONS,
    relocateOptions: data?.willingness_to_relocate_options || FALLBACK_RELOCATE_OPTIONS,
    screeningTemplates: data?.screening_templates?.length
      ? withQuestionIds(data.screening_templates)
      : withQuestionIds(SCREENING_TEMPLATES),
  };
}

type JobFormOptionsState = ReturnType<typeof buildJobFormOptions>;

function normalizeScreeningQuestions(values: any, fallback: ScreeningQuestion[]): ScreeningQuestion[] {
  if (!Array.isArray(values) || values.length === 0) return fallback;
  return values.map((q: any) => ({
    id: q.id ? String(q.id) : generateId(),
    text: String(q.text ?? ''),
    options: Array.isArray(q.options) ? q.options.map(String) : [],
    mandatory: q.mandatory ?? false,
    type: q.type ?? 'text',
    enabled: q.enabled ?? true,
  }));
}

// ─── Helper functions ─────────────────────────────────────────────────────────

function decodeHtml(html: string) {
  if (!html) return '';
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

function toStringArray(val: any): string[] {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(String).filter(Boolean);
  return [];
}

function normalizeTextList(values: any): string[] {
  if (!values) return [];
  const source = Array.isArray(values) ? values : [values];
  return source
    .map((item) => {
      if (item && typeof item === 'object') {
        return String(item.title ?? item.name ?? item.label ?? item.description ?? '').trim();
      }
      return String(item ?? '').trim();
    })
    .filter(Boolean);
}

function normalizeMatchText(value: any): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function hasMatchedText(values: string[], target: string): boolean {
  const targetKey = normalizeMatchText(target);
  if (!targetKey) return false;
  return values.some((value) => normalizeMatchText(value) === targetKey);
}

function normalizeIndustryNames(values: any): string[] {
  if (!values) return [];
  const source = Array.isArray(values) ? values : [values];
  const seen = new Set<string>();
  return source
    .flatMap((item: any) => {
      if (typeof item === 'string') return item.split(',').map((e) => e.trim());
      if (item && typeof item === 'object') {
        const v = String(item.name ?? item.industry_name ?? item.label ?? '').trim();
        return v ? [v] : [];
      }
      return [];
    })
    .filter((item: string) => {
      const key = item.toLowerCase();
      if (!item || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function areSameTextLists(a: string[], b: string[]): boolean {
  const na = normalizeTextList(a).map((i) => i.toLowerCase()).sort();
  const nb = normalizeTextList(b).map((i) => i.toLowerCase()).sort();
  if (na.length !== nb.length) return false;
  return na.every((item, idx) => item === nb[idx]);
}

function generateId() {
  return Math.random().toString(36).substr(2, 9);
}

function getRichTextPlainText(value: string): string {
  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .trim();
}

function sanitizeRichTextPreview(html: string): string {
  if (!html) return '';
  return html
    // Remove newlines between HTML tags so pre-wrap or whitespace doesn't introduce unwanted gaps
    .replace(/>\s*\n\s*</g, '><')
    // Collapse multiple consecutive empty paragraphs into a single one
    .replace(/(?:<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>|\u00a0)*<\/p>\s*){2,}/gi, '<p><br></p>')
    // Strip trailing empty paragraphs, breaks, and whitespace
    .replace(/(?:<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>|\u00a0)*<\/p>|<br\s*\/?>|\s)+$/gi, '')
    // Strip leading empty paragraphs, breaks, and whitespace
    .replace(/^(?:<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>|\u00a0)*<\/p>|<br\s*\/?>|\s)+/gi, '')
    .trim();
}

function getWordCount(value: string): number {
  const plainText = getRichTextPlainText(value)
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!plainText) return 0;
  return plainText.split(' ').filter(Boolean).length;
}

// WITH this fixed version
function getWordCountFromPlainText(value: string): number {
  const plainText = String(value ?? '')
    .replace(/\n/g, ' ')        // ← newlines ko space banao
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!plainText) return 0;
  if (!plainText) return 0;
  return plainText.split(' ').filter(w => w.trim().length > 0).length;
}

// Word limit enforcement is handled directly in the editor change handler.

// ─── Modern UI Components ─────────────────────────────────────────────────────

const FloatingLabelInput: React.FC<{
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  error?: string;
  required?: boolean;
  icon?: React.ReactNode;
  helperText?: string;
}> = ({ label, value, onChange, placeholder, type = 'text', error, required, icon, helperText }) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = String(value).length > 0;
  const hasFloatingLabel = Boolean(label && label.trim().length > 0);

  return (
    <div className="ajm-field-modern">
      <div className={`ajm-input-wrapper ${error ? 'ajm-input-wrapper--error' : ''} ${isFocused ? 'ajm-input-wrapper--focused' : ''}`}>
        {icon && <span className="ajm-input-icon">{icon}</span>}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={hasFloatingLabel ? (isFocused ? placeholder : '') : (placeholder || '')}
          className="ajm-input-modern"
        />
        {hasFloatingLabel && (
          <label className={`ajm-floating-label ${hasValue || isFocused ? 'ajm-floating-label--active' : ''}`}>
            {label}{required && <span className="ajm-required">*</span>}
          </label>
        )}
        {value && (
          <button
            type="button"
            className="ajm-input-clear-btn"
            onClick={() => onChange('')}
          >
            <X size={14} />
          </button>
        )}
      </div>
      {helperText && !error && <span className="ajm-helper-text">{helperText}</span>}
      {error && <span className="ajm-error-text">{error}</span>}
    </div>
  );
};

const CurrencySelect: React.FC<{
  options: SalaryCurrencyOption[];
  value: string;
  onChange: (value: string) => void;
}> = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    } else {
      setSearchQuery('');
    }
  }, [open]);

  const selectedOption = options.find((option) => option.value === value) || options[0];

  const filteredOptions = options.filter((option) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    const valMatch = option.value.toLowerCase().includes(q);
    const labelMatch = option.label?.toLowerCase().includes(q);
    const symbolMatch = option.symbol?.toLowerCase().includes(q);
    return valMatch || labelMatch || symbolMatch;
  });

  return (
    <div className="ajm-currency-select-wrap" ref={ref}>
      <button
        type="button"
        className={`ajm-currency-select ajm-currency-select-btn ${open ? 'ajm-currency-select-btn--open' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="ajm-currency-select-value">
          {selectedOption?.iconUrl ? (
            <img
              src={selectedOption.iconUrl}
              alt={`${selectedOption.value} icon`}
              className="ajm-currency-icon"
            />
          ) : null}
          <span>{selectedOption?.value || value}</span>
        </span>
        <ChevronDown size={16} className={`ajm-currency-chevron ${open ? 'ajm-currency-chevron--open' : ''}`} />
      </button>

      {open && (
        <div className="ajm-currency-dropdown" style={{ minWidth: "180px", padding: 0 }}>
          <div style={{ padding: "8px", borderBottom: "1px solid #EEF2F7", position: "sticky", top: 0, background: "#FFFFFF", zIndex: 2 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "8px", padding: "4px 8px" }}>
              <Search size={14} style={{ color: "#94A3B8", flexShrink: 0 }} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Search currency..."
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: "12px",
                  width: "100%",
                  color: "#1E293B",
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchQuery('');
                  }}
                  style={{ border: "none", background: "none", padding: 0, cursor: "pointer", color: "#94A3B8", display: "flex", alignItems: "center" }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          <div style={{ maxHeight: "200px", overflowY: "auto", padding: "4px 0" }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = option.value === value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`ajm-currency-option ${isSelected ? 'ajm-currency-option--selected' : ''}`}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                      setSearchQuery('');
                    }}
                  >
                    <span className="ajm-currency-option-main">
                      {option.iconUrl ? (
                        <img
                          src={option.iconUrl}
                          alt={`${option.value} icon`}
                          className="ajm-currency-icon"
                        />
                      ) : null}
                      <span>{option.value}</span>
                    </span>
                    <span className="ajm-currency-option-meta">
                      {option.symbol ? option.symbol : option.label}
                    </span>
                  </button>
                );
              })
            ) : (
              <div style={{ padding: "12px 14px", fontSize: "12px", color: "#94A3B8", textAlign: "center" }}>
                No currency found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SelectButtonGroup: React.FC<{
  options: Array<{ value: string; label: string; icon?: string }>;
  value: string | string[];
  onChange: (value: any) => void;
  multiple?: boolean;
  columns?: number;
}> = ({ options, value, onChange, multiple, columns = 2 }) => {
  const isSelected = (val: string) =>
    multiple ? (value as string[]).includes(val) : value === val;

  const handleClick = (val: string) => {
    if (multiple) {
      const current = value as string[];
      onChange(current.includes(val)
        ? current.filter(v => v !== val)
        : [...current, val]
      );
    } else {
      onChange(val);
    }
  };

  return (
    <div className="ajm-select-grid" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`ajm-select-btn ${isSelected(opt.value) ? 'ajm-select-btn--active' : ''}`}
          onClick={() => handleClick(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};

const CardSelector: React.FC<{
  options: Array<{ value: string; label: string; description?: string }>;
  value: string;
  onChange: (value: string) => void;
}> = ({ options, value, onChange }) => (
  <div className="ajm-card-selector">
    {options.map((opt) => (
      <div
        key={opt.value}
        className={`ajm-card-option ${value === opt.value ? 'ajm-card-option--active' : ''}`}
        onClick={() => onChange(opt.value)}
      >
        <div className="ajm-card-option__radio">
          {value === opt.value && <Check size={14} />}
        </div>
        <div className="ajm-card-option__content">
          <div className="ajm-card-option__label">{opt.label}</div>
          {opt.description && (
            <div className="ajm-card-option__desc">{opt.description}</div>
          )}
        </div>
      </div>
    ))}
  </div>
);

/* ── Simple styled dropdown (replaces native <select>) ── */
const SimpleDropdown: React.FC<{
  value: string;
  onChange: (val: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  icon?: React.ReactNode;
}> = ({ value, onChange, options, placeholder = 'Select...', icon }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedOption = options.find((o) => o.value === value);

  return (
    <div className="ajm-industry-select" ref={ref}>
      <div className="ajm-industry-trigger" onClick={() => setOpen((o) => !o)}>
        {icon && <span className="ajm-simple-dd-icon">{icon}</span>}
        {selectedOption
          ? <span className="ajm-industry-tags-preview">{selectedOption.label}</span>
          : <span className="ajm-industry-placeholder">{placeholder}</span>
        }
        <ChevronDown size={16} className={`ajm-industry-chevron ${open ? 'ajm-industry-chevron--open' : ''}`} />
      </div>
      {open && (
        <div className="ajm-industry-dropdown ajm-simple-dd-list">
          <div
            className={`ajm-industry-item ${!value ? 'ajm-industry-item--checked' : ''}`}
            onClick={() => { onChange(''); setOpen(false); }}
          >
            <span className={`ajm-industry-checkbox ${!value ? 'ajm-industry-checkbox--checked' : ''}`}>
              {!value && <Check size={11} color="#fff" strokeWidth={3} />}
            </span>
            <span className="ajm-industry-item-label" style={{ color: 'var(--ajm-gray-400)' }}>{placeholder}</span>
          </div>
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`ajm-industry-item ${value === opt.value ? 'ajm-industry-item--checked' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              <span className={`ajm-industry-checkbox ${value === opt.value ? 'ajm-industry-checkbox--checked' : ''}`}>
                {value === opt.value && <Check size={11} color="#fff" strokeWidth={3} />}
              </span>
              <span className="ajm-industry-item-label">{opt.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Generic single-select dropdown with custom option + search ── */
const CustomSingleSelect: React.FC<{
  value: string;
  onChange: (val: string) => void;
  options: Array<string | { value: string; label: string; icon?: React.ReactNode }>;
  placeholder?: string;
  icon?: React.ReactNode;
  searchable?: boolean;
  allowCustom?: boolean;
}> = ({ value, onChange, options, placeholder = 'Select...', icon, searchable = true, allowCustom = false }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [customInput, setCustomInput] = useState('');
  const [extraOptions, setExtraOptions] = useState<Array<{ value: string; label: string }>>([]);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const normalizedOptions = [
    ...options.map((option) => typeof option === 'string' ? { value: option, label: option } : option),
    ...extraOptions,
  ];
  const allOpts = normalizedOptions.filter(
    (option, index, self) => self.findIndex((item) => item.value === option.value) === index
  );
  const filtered = allOpts.filter(option => option.label.toLowerCase().includes(search.toLowerCase()));
  const selectedOption = allOpts.find((option) => option.value === value);

  const addCustom = () => {
    const v = customInput.trim();
    if (v && !allOpts.some((option) => option.value === v)) setExtraOptions(prev => [...prev, { value: v, label: v }]);
    if (v) onChange(v);
    setCustomInput('');
    setOpen(false);
  };

  return (
    <div className="ajm-industry-select" ref={ref}>
      <div className="ajm-industry-trigger" onClick={() => setOpen(o => !o)}>
        {value
          ? <span className="ajm-industry-tags-preview">{value}</span>
          : <span className="ajm-industry-placeholder">{placeholder}</span>
        }
        <ChevronDown size={16} className={`ajm-industry-chevron ${open ? 'ajm-industry-chevron--open' : ''}`} />
      </div>
      {open && (
        <div className="ajm-industry-dropdown">
          <div className="ajm-industry-search-row">
            <Search size={14} className="ajm-industry-search-icon" />
            <input autoFocus className="ajm-industry-search-input" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
            {value && <button type="button" className="ajm-industry-clear" onClick={() => { onChange(''); setOpen(false); }}>Clear</button>}
          </div>
          <div className="ajm-industry-list">
            {filtered.map(opt => (
              <div
                key={opt.value}
                className={`ajm-industry-item ${value === opt.value ? 'ajm-industry-item--checked' : ''}`}
                onClick={() => { onChange(opt.value); setOpen(false); setSearch(''); }}
              >
                <span className={`ajm-industry-checkbox ${value === opt.value ? 'ajm-industry-checkbox--checked' : ''}`}>
                  {value === opt.value && <Check size={11} color="#fff" strokeWidth={3} />}
                </span>
                <span className="ajm-industry-item-label">{opt.label}</span>
              </div>
            ))}
          </div>
          <div className="ajm-industry-custom">
            <div className="ajm-industry-custom-label">✏️ ADD CUSTOM OPTION</div>
            <div className="ajm-industry-custom-row">
              <input
                className="ajm-industry-custom-input"
                placeholder="Type custom option & press Enter..."
                value={customInput}
                onChange={e => setCustomInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
              />
              <button type="button" className="ajm-industry-add-btn" onClick={addCustom}>+ Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Generic multi-select dropdown with checkboxes + custom option ── */
const CustomMultiSelect: React.FC<{
  selected: string[];
  onChange: (vals: string[]) => void;
  options: string[];
  placeholder?: string;
}> = ({ selected, onChange, options, placeholder = 'Select...' }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [customInput, setCustomInput] = useState('');
  const [extraOptions, setExtraOptions] = useState<string[]>([]);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allOpts = Array.from(new Set([...options, ...extraOptions, ...(Array.isArray(selected) ? selected.filter(s => !options.includes(s)) : [])]));
  const filtered = allOpts.filter(o => o.toLowerCase().includes(search.toLowerCase()));

  const toggle = (val: string) =>
    onChange(selected.includes(val) ? selected.filter(s => s !== val) : [...selected, val]);

  const addCustom = () => {
    const v = customInput.trim();
    if (v && !allOpts.includes(v)) setExtraOptions(prev => [...prev, v]);
    if (v && !selected.includes(v)) onChange([...selected, v]);
    setCustomInput('');
  };

  return (
    <div className="ajm-industry-select" ref={ref}>
      <div className="ajm-industry-trigger" onClick={() => setOpen(o => !o)}>
        {selected.length === 0
          ? <span className="ajm-industry-placeholder">{placeholder}</span>
          : (
            <div className="ajm-industry-tags-preview">
              {(Array.isArray(selected) ? selected : [selected]).map((val) => (
                <span key={val} className="ajm-industry-chip" onClick={(e) => e.stopPropagation()}>
                  <span className="ajm-industry-chip__label">{val}</span>
                  <button
                    type="button"
                    className="ajm-industry-chip__remove"
                    onClick={(e) => { e.stopPropagation(); toggle(val); }}
                    aria-label={`Remove ${val}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )
        }
        <ChevronDown size={16} className={`ajm-industry-chevron ${open ? 'ajm-industry-chevron--open' : ''}`} />
      </div>
      {open && (
        <div className="ajm-industry-dropdown">
          <div className="ajm-industry-search-row">
            <Search size={14} className="ajm-industry-search-icon" />
            <input autoFocus className="ajm-industry-search-input" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
            {selected.length > 0 && <button type="button" className="ajm-industry-clear" onClick={() => onChange([])}>Clear all</button>}
          </div>
          {selected.length > 0 && <div className="ajm-industry-count">{selected.length} selected</div>}
          <div className="ajm-industry-list">
            {filtered.map(opt => {
              const checked = selected.includes(opt);
              return (
                <div key={opt} className={`ajm-industry-item ${checked ? 'ajm-industry-item--checked' : ''}`} onClick={() => toggle(opt)}>
                  <span className={`ajm-industry-checkbox ${checked ? 'ajm-industry-checkbox--checked' : ''}`}>
                    {checked && <Check size={11} color="#fff" strokeWidth={3} />}
                  </span>
                  <span className="ajm-industry-item-label">{opt}</span>
                </div>
              );
            })}
          </div>
          <div className="ajm-industry-custom">
            <div className="ajm-industry-custom-label">✏️ ADD CUSTOM OPTION</div>
            <div className="ajm-industry-custom-row">
              <input
                className="ajm-industry-custom-input"
                placeholder="Type custom option & press Enter..."
                value={customInput}
                onChange={e => setCustomInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
              />
              <button type="button" className="ajm-industry-add-btn" onClick={addCustom}>+ Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Skills multi-select with API search + checkboxes + custom add ── */
const SkillsMultiSelect: React.FC<{
  selected: SkillsListArray[];
  onChange: (vals: SkillsListArray[]) => void;
  allSkills: SkillsListArray[];
  isLoading: boolean;
  onSearch: (val: string) => void;
}> = ({ selected, onChange, allSkills, isLoading, onSearch }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [customInput, setCustomInput] = useState('');
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = (val: string) => {
    setSearch(val);
    onSearch(val);
  };

  // Merge API results with already-selected (so selected stay visible)
  const merged = Array.from(
    new Map([...selected, ...allSkills].map(s => [s.id, s])).values()
  );
  const filtered = search.trim()
    ? merged.filter(s => s.name.toLowerCase().includes(search.toLowerCase()))
    : merged;

  const isChecked = (s: SkillsListArray) => selected.some(sel => sel.id === s.id);

  const toggle = (s: SkillsListArray) =>
    onChange(isChecked(s) ? selected.filter(sel => sel.id !== s.id) : [...selected, s]);

  const removeChip = (s: SkillsListArray, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter(sel => sel.id !== s.id));
  };

  const addCustom = () => {
    const v = customInput.trim();
    if (!v) return;
    const custom: SkillsListArray = { id: `custom_${v}` as any, name: v };
    if (!selected.some(s => s.name.toLowerCase() === v.toLowerCase())) {
      onChange([...selected, custom]);
    }
    setCustomInput('');
  };

  return (
    <div className="ajm-industry-select" ref={ref}>
      {/* Trigger — chips when selected, placeholder when empty */}
      <div className="ajm-industry-trigger" onClick={() => setOpen(o => !o)}>
        {selected.length === 0
          ? <span className="ajm-industry-placeholder">Search and select skills...</span>
          : (
            <div className="ajm-industry-tags-preview">
              {selected.map((skill) => (
                <span key={skill.id} className="ajm-industry-chip" onClick={(e) => e.stopPropagation()}>
                  <span className="ajm-industry-chip__label">{skill.name}</span>
                  <button
                    type="button"
                    className="ajm-industry-chip__remove"
                    onClick={(e) => removeChip(skill, e)}
                    aria-label={`Remove ${skill.name}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )
        }
        <ChevronDown size={16} className={`ajm-industry-chevron ${open ? 'ajm-industry-chevron--open' : ''}`} />
      </div>

      {open && (
        <div className="ajm-industry-dropdown">
          {/* Search + Clear */}
          <div className="ajm-industry-search-row">
            <Search size={14} className="ajm-industry-search-icon" />
            <input
              autoFocus
              className="ajm-industry-search-input"
              placeholder="Search skills..."
              value={search}
              onChange={e => handleSearch(e.target.value)}
            />
            {selected.length > 0 && (
              <button type="button" className="ajm-industry-clear" onClick={() => onChange([])}>Clear all</button>
            )}
          </div>

          {/* Selected count */}
          {selected.length > 0 && (
            <div className="ajm-industry-count">{selected.length} selected</div>
          )}

          {/* Options list */}
          <div className="ajm-industry-list">
            {isLoading && <div className="ajm-industry-loading">Loading...</div>}
            {!isLoading && filtered.length === 0 && search.trim() === '' && (
              <div className="ajm-industry-empty">Type to search skills...</div>
            )}
            {!isLoading && filtered.length === 0 && search.trim() !== '' && (
              <div className="ajm-industry-empty">No skills found for "{search}"</div>
            )}
            {filtered.map(skill => {
              const checked = isChecked(skill);
              return (
                <div
                  key={skill.id}
                  className={`ajm-industry-item ${checked ? 'ajm-industry-item--checked' : ''}`}
                  onClick={() => toggle(skill)}
                >
                  <span className={`ajm-industry-checkbox ${checked ? 'ajm-industry-checkbox--checked' : ''}`}>
                    {checked && <Check size={11} color="#fff" strokeWidth={3} />}
                  </span>
                  <span className="ajm-industry-item-label">{skill.name}</span>
                </div>
              );
            })}
          </div>

          {/* Add custom skill */}
          <div className="ajm-industry-custom">
            <div className="ajm-industry-custom-label">✏️ ADD CUSTOM SKILL</div>
            <div className="ajm-industry-custom-row">
              <input
                className="ajm-industry-custom-input"
                placeholder="Type custom skill & press Enter..."
                value={customInput}
                onChange={e => setCustomInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
              />
              <button type="button" className="ajm-industry-add-btn" onClick={addCustom}>+ Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Question Card ── */
const QuestionCard: React.FC<{
  q: ScreeningQuestion;
  index: number;
  onUpdate: (patch: Partial<ScreeningQuestion>) => void;
  onRemove: () => void;
  onAddOption: (opt: string) => void;
  onRemoveOption: (optIdx: number) => void;
}> = ({ q, index, onUpdate }) => {
  return (
    <div className={`ajm-question-card ${!q.enabled ? 'ajm-question-card--disabled' : ''}`}>
      <div className="ajm-question-row">
        <div className="ajm-question-number">{index + 1}</div>
        <span className="ajm-question-text-static">{q.text}</span>
        {/* Toggle */}
        <button
          type="button"
          className={`ajm-question-toggle ${q.enabled ? 'ajm-question-toggle--on' : 'ajm-question-toggle--off'}`}
          onClick={() => onUpdate({ enabled: !q.enabled })}
          title={q.enabled ? 'Disable question' : 'Enable question'}
        >
          <span className="ajm-question-toggle-knob" />
        </button>
      </div>
    </div>
  );
};

const IndustryMultiSelect: React.FC<{
  selected: string[];
  onChange: (vals: string[]) => void;
  allIndustries: { id: number; name: string }[];
}> = ({ selected, onChange, allIndustries }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [customInput, setCustomInput] = useState('');
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const industryNames = allIndustries.map(i => i.name);
  const allOptions = Array.from(new Set([...industryNames, ...selected.filter(s => !industryNames.includes(s))]));
  const filtered = allOptions.filter(n => n.toLowerCase().includes(search.toLowerCase()));

  const toggle = (name: string) => {
    onChange(selected.includes(name) ? selected.filter(s => s !== name) : [...selected, name]);
  };

  const addCustom = () => {
    const v = customInput.trim();
    if (v && !selected.includes(v)) onChange([...selected, v]);
    setCustomInput('');
  };

  return (
    <div className="ajm-industry-select" ref={ref}>
      <div className="ajm-industry-trigger" onClick={() => setOpen(o => !o)}>
        {selected.length === 0
          ? <span className="ajm-industry-placeholder">Select industries...</span>
          : (
            <div className="ajm-industry-tags-preview">
              {selected.map((name) => (
                <span
                  key={name}
                  className="ajm-industry-chip"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="ajm-industry-chip__label">{name}</span>
                  <button
                    type="button"
                    className="ajm-industry-chip__remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(name);
                    }}
                    aria-label={`Remove ${name}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )
        }
        <ChevronDown size={16} className={`ajm-industry-chevron ${open ? 'ajm-industry-chevron--open' : ''}`} />
      </div>

      {open && (
        <div className="ajm-industry-dropdown">
          {/* Search + Clear */}
          <div className="ajm-industry-search-row">
            <Search size={14} className="ajm-industry-search-icon" />
            <input
              autoFocus
              className="ajm-industry-search-input"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {selected.length > 0 && (
              <button type="button" className="ajm-industry-clear" onClick={() => onChange([])}>Clear all</button>
            )}
          </div>

          {/* Selected count */}
          {selected.length > 0 && (
            <div className="ajm-industry-count">{selected.length} selected</div>
          )}

          {/* Options list */}
          <div className="ajm-industry-list">
            {filtered.map(name => {
              const checked = selected.includes(name);
              return (
                <div
                  key={name}
                  className={`ajm-industry-item ${checked ? 'ajm-industry-item--checked' : ''}`}
                  onClick={() => toggle(name)}
                >
                  <span className={`ajm-industry-checkbox ${checked ? 'ajm-industry-checkbox--checked' : ''}`}>
                    {checked && <Check size={11} color="#fff" strokeWidth={3} />}
                  </span>
                  <span className="ajm-industry-item-label">{name}</span>
                </div>
              );
            })}
          </div>

          {/* Add custom */}
          <div className="ajm-industry-custom">
            <div className="ajm-industry-custom-label">✏️ ADD CUSTOM INDUSTRY</div>
            <div className="ajm-industry-custom-row">
              <input
                className="ajm-industry-custom-input"
                placeholder="Type custom industry & press Enter..."
                value={customInput}
                onChange={e => setCustomInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
              />
              <button type="button" className="ajm-industry-add-btn" onClick={addCustom}>+ Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RangeInput: React.FC<{
  label: string;
  minValue: string | number;
  maxValue: string | number;
  onMinChange: (val: string) => void;
  onMaxChange: (val: string) => void;
  options: Array<{ value: string | number; label: string }>;
  error?: string;
  unit?: string;
}> = ({ label, minValue, maxValue, onMinChange, onMaxChange, options, error, unit }) => (
  <div className="ajm-range-field">
    <label className="ajm-range-label">{label}</label>
    <div className="ajm-range-inputs">
      <div className="ajm-range-input-group">
        <select
          value={minValue}
          onChange={(e) => onMinChange(e.target.value)}
          className="ajm-range-select"
        >
          <option value="">Min {unit}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      <span className="ajm-range-separator">to</span>
      <div className="ajm-range-input-group">
        <select
          value={maxValue}
          onChange={(e) => onMaxChange(e.target.value)}
          className="ajm-range-select"
        >
          <option value="">Max {unit}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    </div>
    {/* {error && <span className="ajm-error-text">{error}</span>} */}
  </div>
);

const ToggleSwitch: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
  variant?: 'default' | 'danger';
}> = ({ checked, onChange, label, description, variant = 'default' }) => (
  <div className="ajm-toggle-modern">
    <div className="ajm-toggle-content">
      <div className="ajm-toggle-label">{label}</div>
      {description && <div className="ajm-toggle-desc">{description}</div>}
    </div>
    <button
      type="button"
      className={`ajm-toggle-switch ${checked ? 'ajm-toggle-switch--on' : ''} ${checked && variant === 'danger' ? 'ajm-toggle-switch--danger' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span className="ajm-toggle-thumb">
      </span>
    </button>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const AddJobModal: React.FC<AddJobModalProps> = ({
  onCancel,
  handleAddJob,
  jobDetails,
  companyBenefits = [],
  onAutoDraft,
}) => {
  const isEditingExistingJob = Boolean(jobDetails?.id);
  const currentJobIsDraft = Boolean(jobDetails?.is_draft);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [jobFormOptions, setJobFormOptions] = useState<JobFormOptionsState>(() => buildJobFormOptions());
  const [isLoadingFormOptions, setIsLoadingFormOptions] = useState(true);

  // ── Wizard step state ──
  const [activeStep, setActiveStep] = useState<StepIndex>(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');

  // ── Step 1 state ──
  const [openDesignationList, setOpenDesignationList] = useState(false);
  const [allDesignationList, setAllDesignationList] = useState<DesignationArray[]>([]);
  const [isLoadingDesignations, setIsLoadingDesignations] = useState(false);
  const [allWorkExperienceList, setAllWorkExperienceList] = useState<any[]>([]);
  const [selectedPerks, setSelectedPerks] = useState<string[]>([]);
  const [step1Errors, setStep1Errors] = useState<Record<string, string>>({});
  const [step2Errors, setStep2Errors] = useState<Record<string, string>>({});
  const jobTitleFieldRef = useRef<HTMLDivElement | null>(null);
  const modalBodyRef = useRef<HTMLDivElement | null>(null);
  const state = useSelector((state: RootState) => state.auth);
  const isDomainVerified = state.verified_domain;
  const reduxAuthPerks = useSelector((state: RootState) => (state.auth as any)?.user?.company_perks || (state.auth as any)?.user?.perks);
  const reduxOnboardingPerks = useSelector((state: RootState) => (state.onboarding as any)?.companyDetails?.perks || (state.onboarding as any)?.companyIntroVision?.perks);
  const reduxCompanyPerks = useMemo(
    () => normalizeTextList(reduxAuthPerks || reduxOnboardingPerks),
    [reduxAuthPerks, reduxOnboardingPerks]
  );
  const quillRef = useRef<ReactQuill>(null);

  // Normalized company benefits for pre-filling perks & benefits
  const jobDetailsCompanyBenefits = jobDetails?.company_benefits;
  const normalizedCompanyBenefits = useMemo(
    () => normalizeTextList(
      Array.isArray(jobDetailsCompanyBenefits) ? jobDetailsCompanyBenefits : companyBenefits
    ),
    [jobDetailsCompanyBenefits, companyBenefits]
  );

  const {
    isOpen: isCreditPopupOpen,
    popupOptions: creditPopupOptions,
    requestConfirm,
    handleConfirm: handleCreditConfirm,
    handleCancel: handleCreditCancel,
    handleNeverShowAgain: handleCreditNeverShowAgain,
  } = useCreditConfirm();

  // Forward modal body scroll to window so Google Places pac-container repositions
  useEffect(() => {
    const el = modalBodyRef.current;
    if (!el) return;
    const onScroll = () => window.dispatchEvent(new Event('scroll', { bubbles: true }));
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // ── Step 2 state ──
  const [department, setDepartment] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [suggestedPage, setSuggestedPage] = useState(1);
  const [suggestedTotalPages, setSuggestedTotalPages] = useState(1);
  const parseQualification = (val: any): string[] => {
    if (!val) return [];

    // If already array
    if (Array.isArray(val)) return val.map(String);

    // If string like '["12th Pass","Diploma"]'
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch (e) { }

    // fallback (comma separated)
    return String(val)
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);
  };

  const [qualification, setQualification] = useState<string[]>(
    parseQualification(jobDetails?.qualification)
  );
  // const [qualification, setQualification] = useState<string[]>(jobDetails?.qualification.join(',') ?? []);
  const [genderPreference, setGenderPreference] = useState<string>(() => {
    const raw = jobDetails?.gender_preference;
    if (!raw) return 'Any';
    // API may return ['female'] or 'female'
    if (Array.isArray(raw)) return raw[0] || 'Any';
    return String(raw);
  });
  const [candidateIndustries, setCandidateIndustries] = useState<string[]>([]);

  // ── Step 2 + 1 shared: skills ──
  const [allSkillsList, setAllSkillsList] = useState<SkillsListArray[]>([]);
  const [searchSkill, setSearchSkill] = useState('');
  const [isLoadingSkills, setIsLoadingSkills] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState<SkillsListArray[]>([]);
  const [suggestedSkills, setSuggestedSkills] = useState<SkillsListArray[]>([]);
  const [isLoadingSkillsByIndustries, setIsLoadingSkillsByIndustries] = useState(false);
  const [showAllSuggestedSkills, setShowAllSuggestedSkills] = useState(false);
  const [industryIdsForSuggestions, setIndustryIdsForSuggestions] = useState<number[]>([]);
  const [allIndustries, setAllIndustries] = useState<{ id: number; name: string }[]>([]);
  const [companyIndustries, setCompanyIndustries] = useState<string[]>([]);

  // ── Step 3 state ──
  const [screeningQuestions, setScreeningQuestions] = useState<ScreeningQuestion[]>(() =>
    normalizeScreeningQuestions(jobDetails?.screening_questions, jobFormOptions.screeningTemplates)
  );
  // ── Step 3 extras ──
  const [autoRejectRules, setAutoRejectRules] = useState<string[]>(jobDetails?.auto_reject_rules ?? []);

  // ── Step 4 state ──
  const [description, setDescription] = useState(
    jobDetails?.description ? decodeHtml(jobDetails.description) : ''
  );
  const [step4Errors, setStep4Errors] = useState<Record<string, string>>({});
  const [aboutCompany, setAboutCompany] = useState(jobDetails?.about_company || '');
  const computeInitialPerks = useCallback(() => {
    const jobPerks = normalizeTextList(jobDetails?.perks);
    if (jobPerks.length) return jobPerks;
    if (normalizedCompanyBenefits.length) return normalizedCompanyBenefits;
    if (reduxCompanyPerks.length) return reduxCompanyPerks;
    return [''];
  }, [jobDetails?.perks, normalizedCompanyBenefits, reduxCompanyPerks]);

  const [perksSection, setPerksSection] = useState<string[]>(computeInitialPerks);
  const [careersPageLink, setCareersPageLink] = useState(jobDetails?.careers_page_link || '');
  const [responsibilities, setResponsibilities] = useState<string[]>(
    jobDetails?.responsibilities?.length ? jobDetails.responsibilities.map((r: string) => decodeHtml(r)) : ['']
  );
  const [requirements, setRequirements] = useState<string[]>(
    jobDetails?.requirements?.length ? jobDetails.requirements.map((r: string) => decodeHtml(r)) : ['']
  );
  const [dragItemIndex, setDragItemIndex] = useState<{ list: 'responsibilities' | 'requirements' | 'benefits', index: number } | null>(null);

  // ── Step 2 extras ──
  const [languageRequirements, setLanguageRequirements] = useState<string[]>(
    jobDetails?.language_requirements?.length ? jobDetails.language_requirements : []
  );


  const [willingnessToRelocate, setWillingnessToRelocate] = useState<RelocateValue>(jobDetails?.willingness_to_relocate || 'negotiable');
  const [ageMin, setAgeMin] = useState<number | ''>(() => {
    const v = jobDetails?.age_min;
    return (v !== null && v !== undefined && v !== 0) ? v : '';
  });
  const [ageMax, setAgeMax] = useState<number | ''>(() => {
    const v = jobDetails?.age_max;
    return (v !== null && v !== undefined && v !== 0) ? v : '';
  });

  // ── Step 5 state ──
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyEmails, setNotifyEmails] = useState<string[]>(jobDetails?.notify_emails ?? ['']);
  const [ackTemplate, setAckTemplate] = useState(jobDetails?.ack_template || '');
  const [notifySms, setNotifySms] = useState(false);
  const [notifyWhatsapp, setNotifyWhatsapp] = useState(true);

  // ── Shared form state ──
  const [jobTitle, setJobTitle] = useState(jobDetails?.job_title || jobDetails?.title || '');
  const [designationId, setDesignationId] = useState(jobDetails?.designation_id || 0);
  const [experienceId, setExperienceId] = useState(String(jobDetails?.experience_id || ''));
  const [minExp, setMinExp] = useState(jobDetails?.work_experience_years ?? '');
  const [maxExp, setMaxExp] = useState('');
  const [employmentTypeIds, setEmploymentTypeIds] = useState<number[]>(() => {
    const raw = jobDetails?.employment_type_ids;

    if (Array.isArray(raw)) {
      return raw
        .map((v: any) => Number(v))
        .filter((v: number) => !isNaN(v) && v > 0);
    }

    if (raw !== undefined && raw !== null) {
      const num = Number(raw);
      return !isNaN(num) && num > 0 ? [num] : [];
    }

    return [];
  });


  const [industryTags, setIndustryTags] = useState<string[]>(
    normalizeIndustryNames(jobDetails?.industry_tags ?? jobDetails?.industry)
  );
  const [salaryMin, setSalaryMin] = useState<number | ''>(jobDetails?.salary_min ?? '');
  const [salaryMax, setSalaryMax] = useState<number | ''>(jobDetails?.salary_max ?? '');
  const [salaryCurrency, setSalaryCurrency] = useState(jobDetails?.salary_currency || 'INR');
  const [salaryPeriod, setSalaryPeriod] = useState(jobDetails?.salary_period || 'month');
  // 'range' = show number inputs, 'not_disclosed' | 'negotiable' | 'competitive' = quick option
  const [salaryVisibility, setSalaryVisibility] = useState<SalaryVisibilityValue>(
    jobDetails?.salary_visibility || ((jobDetails?.salary_min || jobDetails?.salary_max) ? 'range' : 'range')
  );
  const [workMode, setWorkMode] = useState<WorkModeValue>(jobDetails?.work_mode || '');
  const [applicationDeadline, setApplicationDeadline] = useState<string>(
    normalizeDateOnly(jobDetails?.application_deadline)
  );
  const [openingsCount, setOpeningsCount] = useState<number | ''>(jobDetails?.openings_count ?? '');
  const [locationText, setLocationText] = useState(jobDetails?.location_text || '');
  const [locationLat, setLocationLat] = useState<number | null>(jobDetails?.location_lat ?? null);
  const [locationLng, setLocationLng] = useState<number | null>(jobDetails?.location_lng ?? null);
  const [useCurrentJobLocation, setUseCurrentJobLocation] = useState(false);
  const [companyLocation, setCompanyLocation] = useState<{
    text: string;
    lat: number | null;
    lng: number | null;
  } | null>(null);
  // true only when location was selected from Google Places (not free-typed)
  // pre-existing saved location on edit is always valid
  const [locationValidated, setLocationValidated] = useState(!!jobDetails?.location_text);
  const [pendingLocationChange, setPendingLocationChange] = useState<{ address: string; placeDetails?: any } | null>(null);
  const [hasConfirmedJobLocationEdit, setHasConfirmedJobLocationEdit] = useState(true);

  const computeInitialBenefits = () => {
    if (!jobDetails?.id) {
      if (normalizedCompanyBenefits.length) return normalizedCompanyBenefits;
      if (reduxCompanyPerks.length) return reduxCompanyPerks;
      return [''];
    }
    const plain = normalizeTextList(jobDetails?.benefits);
    if (plain.length) return plain;

    const override = jobDetails?.benefits_override;
    if (override === 1 || override === true) {
      const overrideList = normalizeTextList(jobDetails?.job_benefits_override ?? jobDetails?.benefits);
      return overrideList.length ? overrideList : [''];
    }

    const overrideList = normalizeTextList(jobDetails?.job_benefits_override);
    if (overrideList.length) return overrideList;

    if (normalizedCompanyBenefits.length) return normalizedCompanyBenefits;
    if (reduxCompanyPerks.length) return reduxCompanyPerks;
    return plain.length ? plain : [''];
  };

  const [benefits, setBenefits] = useState<string[]>(computeInitialBenefits);
  const [isClosed, setIsClosed] = useState(Boolean(jobDetails?.is_closed || jobDetails?.isClosed));
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isClosingJob, setIsClosingJob] = useState(false);
  const [reopenDeadlineError, setReopenDeadlineError] = useState(false);
  const [isSubmittingBtn, setIsSubmittingBtn] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [persistedJobId, setPersistedJobId] = useState<string | number | null>(jobDetails?.id ?? null);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setPersistedJobId(jobDetails?.id ?? null);
  }, [jobDetails?.id]);

  // TC1: sync location from jobDetails when it changes (e.g. after re-opening edit)
  useEffect(() => {
    if (jobDetails?.location_text) {
      setLocationText(jobDetails.location_text);
      setLocationLat(jobDetails.location_lat ?? null);
      setLocationLng(jobDetails.location_lng ?? null);
      setLocationValidated(true); // pre-existing saved location is valid
    }
  }, [jobDetails?.location_text]);

  const debouncedSkillSearch = useDebounce(searchSkill, 300);
  const visibleSuggestedSkills = showAllSuggestedSkills ? suggestedSkills : suggestedSkills.slice(0, 12);
  const {
    employmentTypeOptions,
    workModeOptions,
    salaryCurrencyOptions,
    salaryPeriodOptions,
    salaryVisibilityOptions,
    qualificationOptions,
    genderOptions,
    departmentOptions,
    perkSuggestions,
    relocateOptions,
  } = jobFormOptions;
  const visibleSalaryVisibilityOptions = salaryVisibilityOptions.filter((option) => option.value === 'range');
  const [currencyOptions, setCurrencyOptions] = useState<SalaryCurrencyOption[]>(salaryCurrencyOptions);

  // ── Effects ──

  useEffect(() => {
    setActiveStep(0);
    if (jobDetails?.id) {
      const initialCompleted = new Set<number>();
      if (jobDetails.job_title || jobDetails.title || jobDetails.id) {
        initialCompleted.add(0);
      }
      if (jobDetails.skills?.length || jobDetails.qualification) {
        initialCompleted.add(1);
      }
      if (jobDetails.description || jobDetails.responsibilities?.length) {
        initialCompleted.add(2);
      }
      if (jobDetails.id) {
        initialCompleted.add(3);
      }
      setCompletedSteps(initialCompleted);
    } else {
      setCompletedSteps(new Set());
    }
  }, [jobDetails?.id]);

  useEffect(() => {
    setCurrencyOptions(salaryCurrencyOptions);
  }, [salaryCurrencyOptions]);

  useEffect(() => {
    const val = jobDetails?.gender_preferences;

    if (Array.isArray(val)) {
      setGenderPreference(val[0] || '');
    } else if (val) {
      setGenderPreference(String(val));
    }
  }, [jobDetails]);
  useEffect(() => {
    let isMounted = true;

    (async () => {
      setIsLoadingFormOptions(true);
      try {
        const response = await apiService.getJobFormOptions();
        const options = buildJobFormOptions(response?.data);
        if (!isMounted) return;

        setJobFormOptions(options);
        setCurrencyOptions(options.salaryCurrencyOptions);
        setScreeningQuestions(
          normalizeScreeningQuestions(jobDetails?.screening_questions, options.screeningTemplates)
        );
      } catch {
        if (!isMounted) return;
        const fallbackOptions = buildJobFormOptions();
        setJobFormOptions(fallbackOptions);
        setCurrencyOptions(fallbackOptions.salaryCurrencyOptions);
        setScreeningQuestions(
          normalizeScreeningQuestions(jobDetails?.screening_questions, fallbackOptions.screeningTemplates)
        );
      } finally {
        if (isMounted) setIsLoadingFormOptions(false);
      }
    })();

    return () => { isMounted = false; };
  }, [jobDetails?.id]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const response = await apiService.getOnboardingCurrencies();


        // Handle various response shapes:
        // { data: [...] }  /  { data: { data: [...] } }  /  { currencies: [...] }  /  [...]
        const raw = response as any;
        const apiCurrencies: any[] =
          Array.isArray(raw) ? raw
            : Array.isArray(raw?.data) ? raw.data
              : Array.isArray(raw?.data?.data) ? raw.data.data
                : Array.isArray(raw?.currencies) ? raw.currencies
                  : Array.isArray(raw?.data?.currencies) ? raw.data.currencies
                    : [];



        if (!isMounted || !apiCurrencies.length) return;

        const nextCurrencyOptions = apiCurrencies
          .map((currency: any) => ({
            value: String(currency?.code || '').trim(),
            label: currency?.symbol ? `${currency.code} (${currency.symbol})` : String(currency?.code || '').trim(),
            symbol: currency?.symbol ? String(currency.symbol) : undefined,
            // Handle various field names for the flag/icon URL
            iconUrl: currency?.icon_url
              ? String(currency.icon_url)
              : currency?.flag_url
                ? String(currency.flag_url)
                : currency?.flag
                  ? String(currency.flag)
                  : currency?.image_url
                    ? String(currency.image_url)
                    : undefined,
          }))
          .filter((currency: SalaryCurrencyOption) => Boolean(currency.value));

        if (nextCurrencyOptions.length) {
          setCurrencyOptions(nextCurrencyOptions);
        }
      } catch {
        // Fall back to job-form option currencies when icon API is unavailable.
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  // ── Resolve skills from jobDetails ──
  // Skills may come back as bare IDs (numbers) after duplicate — resolve names from allSkillsList.
  // Runs when jobDetails changes OR when allSkillsList is populated.
  const [skillsInitialized, setSkillsInitialized] = useState(false);
  
  useEffect(() => {
    setSkillsInitialized(false);
  }, [jobDetails?.id]);

  useEffect(() => {
    if (skillsInitialized) return;
    if (!Array.isArray(jobDetails?.skills) || jobDetails.skills.length === 0) {
      setSkillsInitialized(true);
      return;
    }

    const hasAllObjects = jobDetails.skills.every((s: any) => typeof s === 'object' && s.name);
    if (hasAllObjects) {
      // All skills already have names — use directly
      setSelectedSkills(jobDetails.skills.map((s: any) => ({ id: s.id, name: s.name })));
      setSkillsInitialized(true);
      return;
    }

    // Some or all are bare IDs — try to resolve from allSkillsList
    if (allSkillsList.length > 0) {
      const resolved = jobDetails.skills.map((s: any) => {
        if (typeof s === 'object' && s.name) return { id: s.id, name: s.name };
        const id = typeof s === 'object' ? s.id : s;
        const match = allSkillsList.find((skill) => String(skill.id) === String(id));
        return match ? { id: match.id, name: match.name } : { id, name: String(id) };
      });
      setSelectedSkills(resolved);
      setSkillsInitialized(true);
    } else {
      // allSkillsList not loaded yet — fetch it so we can resolve on next run
      fetchSkills('');
    }
  }, [jobDetails, allSkillsList, skillsInitialized]);
  useEffect(() => {
    const rawIds =
      jobDetails?.employment_type_ids ||
      (Array.isArray(jobDetails?.job_types)
        ? jobDetails.job_types.map((t: any) => t.id || t).filter(Boolean)
        : null) ||
      (Array.isArray(jobDetails?.employment_types)
        ? jobDetails.employment_types.map((t: any) => t.id || t).filter(Boolean)
        : null);

    if (Array.isArray(rawIds)) {
      setEmploymentTypeIds(rawIds.map((id: any) => Number(typeof id === 'object' ? id.id : id)).filter((n: number) => !isNaN(n) && n > 0));
    } else if (typeof rawIds === 'number' && rawIds > 0) {
      setEmploymentTypeIds([rawIds]);
    } else {
      setEmploymentTypeIds([]);
    }
  }, [jobDetails]);

  useEffect(() => {
    setApplicationDeadline(normalizeDateOnly(jobDetails?.application_deadline));
  }, [jobDetails?.application_deadline]);

  useEffect(() => {
    setWorkMode(jobDetails?.work_mode || '');
  }, [jobDetails?.work_mode]);

  useEffect(() => {
    setIndustryTags(normalizeIndustryNames(jobDetails?.industry_tags ?? jobDetails?.industry));
  }, [jobDetails?.industry_tags, jobDetails?.industry]);

  useEffect(() => {
    if (jobDetails?.language_requirements?.length) {
      setLanguageRequirements(jobDetails.language_requirements);
    }
  }, [jobDetails?.language_requirements]);

  // Re-sync benefits and perks when jobDetails changes (handles object format from API)
  useEffect(() => {
    setBenefits(computeInitialBenefits());
  }, [jobDetails?.id]);

  useEffect(() => {
    setPerksSection(computeInitialPerks());
  }, [jobDetails?.id]);

  useEffect(() => {
    if (debouncedSkillSearch?.trim()) fetchSkills(debouncedSkillSearch);
    else setAllSkillsList([]);
  }, [debouncedSkillSearch]);

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!openDesignationList) return;
      const targetNode = event.target as Node;
      if (jobTitleFieldRef.current && !jobTitleFieldRef.current.contains(targetNode)) {
        setOpenDesignationList(false);
        // If editing and user clicked away without picking, restore the saved title
        if (isEditingExistingJob && !jobTitle.trim()) {
          setJobTitle(jobDetails?.job_title || jobDetails?.title || '');
        }
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [openDesignationList, isEditingExistingJob, jobTitle]);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiService.getWorkExperience();
        if (data?.status) setAllWorkExperienceList(data.data);
        else if (data?.message) toast.error(data.message);
      } catch { toast.error('Failed to fetch work experience'); }
    })();
  }, []);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const profileResponse = await apiService.getProfile();
        const profileData =
          (profileResponse as any)?.data?.companeyDetail || (profileResponse as any)?.data || {};
        const industries = normalizeIndustryNames(profileData?.industry ?? profileData?.industry_tags);
        if (isMounted) {
          setCompanyIndustries(industries);

          const profileLocation = profileData.location;
          const profileLat = profileData.enterprise_latitude ?? profileData.latitude;
          const profileLng = profileData.enterprise_longitude ?? profileData.longitude;

          if (profileLocation) {
            setCompanyLocation({
              text: profileLocation,
              lat: (profileLat != null && profileLat !== '') ? Number(profileLat) : null,
              lng: (profileLng != null && profileLng !== '') ? Number(profileLng) : null,
            });
          }

          // Prefill location from enterprise profile if creating a new job
          if (!jobDetails?.location_text && !jobDetails?.location && !jobDetails?.id) {
            if (profileLocation) {
              setLocationText(profileLocation);
              setLocationValidated(true);
            }
            if (profileLat != null && profileLat !== '') {
              setLocationLat(Number(profileLat));
            }
            if (profileLng != null && profileLng !== '') {
              setLocationLng(Number(profileLng));
            }
          }
        }
      } catch { if (isMounted) setCompanyIndustries([]); }
    })();
    return () => { isMounted = false; };
  }, [jobDetails?.location_text, jobDetails?.location, jobDetails?.id]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const data = await apiService.getIndustries('');
        if (isMounted && data?.status && Array.isArray(data.data)) setAllIndustries(data.data);
      } catch { }
    })();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (!allIndustries.length) return;
    const names = normalizeIndustryNames(industryTags.length > 0 ? industryTags : (jobDetails?.industry_tags ?? jobDetails?.industry));
    const ids = names.flatMap((name) => {
      const match = allIndustries.find((i) => i.name.toLowerCase() === name.toLowerCase());
      return match?.id ? [match.id] : [];
    });
    setIndustryIdsForSuggestions(ids);
    setSuggestedPage(1); // reset to first page when industries change
  }, [allIndustries, industryTags, jobDetails?.industry_tags, jobDetails?.industry]);

  useEffect(() => {
    if (!industryIdsForSuggestions.length) { setSuggestedSkills([]); setSuggestedPage(1); setSuggestedTotalPages(1); return; }
    (async () => {
      try {
        setIsLoadingSkillsByIndustries(true);
        setSuggestedSkills([]); // clear old page immediately so UI updates
        const data = await apiService.getSkillsByIndustries(industryIdsForSuggestions, suggestedPage, 10);


        if (data?.status && Array.isArray(data.data.data)) {
          setSuggestedSkills(data.data.data as SkillsListArray[]);
          setSuggestedTotalPages(data.data.pagination?.total_pages ?? 1);
        } else {
          setSuggestedSkills([]);
          setSuggestedTotalPages(1);
        }
      } catch { setSuggestedSkills([]); setSuggestedTotalPages(1); }
      finally { setIsLoadingSkillsByIndustries(false); }
    })();
  }, [industryIdsForSuggestions, suggestedPage]);

  // ── API helpers ──

  const fetchSkills = async (key?: string) => {
    try {
      setIsLoadingSkills(true);
      const data = await apiService.getSkills(key);
      if (data?.status === true) setAllSkillsList(data.data as SkillsListArray[]);
      else { setAllSkillsList([]); if (data?.message) toast.error(data.message); }
    } catch { setAllSkillsList([]); toast.error('Failed to fetch skills'); }
    finally { setIsLoadingSkills(false); }
  };

  const getDesignation = useCallback(async (key: string) => {
    try {
      setIsLoadingDesignations(true);
      const data = await apiService.getDesignations(key);
      if (data?.status) setAllDesignationList(data.data || []);
      else { setAllDesignationList([]); if (data?.message) toast.error(data.message); }
    } catch { setAllDesignationList([]); toast.error('Failed to fetch designations'); }
    finally { setIsLoadingDesignations(false); }
  }, []);

  // ── Step navigation ──

  const canAccessStep = (targetIdx: number): boolean => {
    if (targetIdx === 0) return true;
    if (targetIdx <= activeStep) return true;
    for (let k = 0; k < targetIdx; k++) {
      if (!completedSteps.has(k)) {
        return false;
      }
    }
    return true;
  };

  const handleStepClick = (idx: number) => {
    if (idx === activeStep) return;
    if (!canAccessStep(idx)) return;

    setDirection(idx > activeStep ? 'forward' : 'backward');
    setActiveStep(idx as StepIndex);
  };

  const validateStep1 = (): boolean => {
    const errs: Record<string, string> = {};
    if (!jobTitle.trim()) errs.jobTitle = 'Job title is required';
    if (!employmentTypeIds.length) errs.employmentType = 'Employment type is required';
    if (!industryTags.length) errs.industry = 'At least one industry is required';
    if (!workMode) errs.workMode = 'Work mode is required';
    if (!experienceId) errs.experience = 'Experience required is required';
    if (!locationText.trim()) errs.location = 'Job location is required';
    else if (!locationValidated) errs.location = 'Please select a valid location from the suggestions';
    if (salaryVisibility === 'range') {
      const { max: maxLimit, label: maxLabel } = getMaxSalaryForPeriod(salaryPeriod);
      if (salaryMin === '') errs.salaryMin = 'Minimum salary is required';
      else if (Number(salaryMin) <= 0) errs.salaryMin = 'Minimum salary must be greater than 0';
      else if (Number(salaryMin) > maxLimit) errs.salaryMin = `Minimum salary cannot exceed ${maxLabel}`;

      if (salaryMax === '' || Number(salaryMax) <= 0) errs.salaryMax = 'Maximum salary is required';
      else if (Number(salaryMax) > maxLimit) errs.salaryMax = `Maximum salary cannot exceed ${maxLabel}`;

      if (salaryMin !== '' && salaryMax !== '' && Number(salaryMin) > 0 && Number(salaryMax) <= maxLimit && Number(salaryMax) <= Number(salaryMin))
        errs.salary = 'Maximum salary must be greater than minimum salary';
    }
    if (minExp !== '' && maxExp !== '' && Number(minExp) > Number(maxExp))
      errs.exp = 'Minimum experience cannot exceed maximum';
    setStep1Errors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = (): boolean => {
    const errs: Record<string, string> = {};
    if (selectedSkills.length === 0) errs.skills = 'Required skills are required';
    const qualArr = Array.isArray(qualification) ? qualification : (typeof qualification === 'string' ? [qualification] : []);
    if (qualArr.filter(Boolean).length === 0) errs.qualification = 'Minimum qualification is required';
    const langArr = Array.isArray(languageRequirements)? languageRequirements:(typeof languageRequirements ==='string'?[languageRequirements]:[])
    if (langArr.filter(Boolean).length === 0) errs.language = 'At least one language is required';
    
    setStep2Errors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep4 = (): boolean => {
    const errs: Record<string, string> = {};
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = description;
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    const descriptionCharCount = plainText.length;
    if (!plainText.trim()) errs.description = 'Job description is required';
    else if (descriptionCharCount > 1000) errs.description = 'Job description cannot exceed 1000 characters';
    if (responsibilities.filter(r => r.trim()).length === 0) errs.responsibilities = 'At least one responsibility is required';
    if (requirements.filter(r => r.trim()).length === 0) errs.requirements = 'At least one requirement is required';
    setStep4Errors(errs);
    return Object.keys(errs).length === 0;
  };
  // helper (add above goNext or in utils)
  const formatDateOnly = (date: any) => {
    if (!date) return null;

    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, "0");

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const extractJobIdFromResponse = (response: any): string | number | null => {
    const candidates = [
      response?.data?.id,
      response?.data?.job_position_id,
      response?.data?.job_id,
      response?.data?.insertId,
      response?.data?.data?.id,
      response?.data?.data?.job_position_id,
      response?.data?.data?.job_id,
      response?.id,
      response?.job_position_id,
      response?.job_id,
    ];

    const resolved = candidates.find((value) => value !== undefined && value !== null && value !== "");
    return resolved ?? null;
  };

  // ── Save current step (shared logic) ──
  const saveCurrentStep = async (status?:string): Promise<boolean> => {
    // Validate all steps up to and including the current one
    // No toast — field-level errors are shown inline, just navigate to the failing step
    if (activeStep >= 0 && !validateStep1()) {
      if (activeStep !== 0) {
        setDirection('backward');
        setActiveStep(0);
      }
      // Scroll to first error field
      setTimeout(() => {
        const firstError = document.querySelector('.ajm-error-text, .ajm-control-shell--error, .ajm-salary-input--error');
        firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return false;
    }
    if (activeStep >= 1 && !validateStep2()) {
      if (activeStep !== 1) {
        setDirection('backward');
        setActiveStep(1);
      }
      setTimeout(() => {
        const firstError = document.querySelector('.ajm-error-text, .ajm-control-shell--error');
        firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return false;
    }
    if (activeStep >= 2 && !validateStep4()) {
      if (activeStep !== 2) {
        setDirection('backward');
        setActiveStep(2);
      }
      setTimeout(() => {
        const firstError = document.querySelector('.ajm-error-text, .ajm-control-shell--error');
        firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return false;
    }

    try {
      let currentJobId = persistedJobId ?? jobDetails?.id ?? null;

      // ── STEP 1 → BASICS (run if not yet completed or currently on step 1) ──
      if (activeStep >= 0 && (!completedSteps.has(0) || activeStep === 0)) {
        const payload: any = {
          id: currentJobId ?? undefined,
          is_draft: true,
          designation_id: designationId,
          job_title: jobTitle,
          employment_type_ids: employmentTypeIds,
          industry_tags: industryTags,
          work_mode: workMode,
          experience_id: experienceId,
          work_experience_years: Number(minExp) || 0,
          work_experience_months: 0,
          salary_min: salaryVisibility === 'range' ? Number(salaryMin) : null,
          salary_max: salaryVisibility === 'range' ? Number(salaryMax) : null,
          salary_currency: salaryCurrency,
          salary_period: salaryPeriod,
          salary_visibility: salaryVisibility,
          openings_count: openingsCount || 1,
          location_text: locationText,
          location_lat: locationLat,
          location_lng: locationLng,
          application_deadline: formatDateOnly(applicationDeadline),
          is_closed: isClosed,
        };

        const res = await apiService.addLookingForWithStep(payload, 'basics');
        const nextJobId = extractJobIdFromResponse(res) ?? currentJobId;
        if (nextJobId == null) {
          toast.error('Unable to continue: basics step did not return a job ID.');
          return false;
        }
        currentJobId = nextJobId;
        setPersistedJobId(nextJobId);
        setCompletedSteps(prev => new Set(prev).add(0));
      }

      if (activeStep >= 1 && currentJobId == null) {
        toast.error('Job ID missing. Please complete Step 1 again.');
        setDirection('backward');
        setActiveStep(0);
        return false;
      }

      // ── STEP 2 → PREFERENCES (run if not yet completed or currently on step 2) ──
      if (activeStep >= 1 && (!completedSteps.has(1) || activeStep === 1)) {
        const payload: any = {
          id: currentJobId,
          is_draft: true,
          skills: selectedSkills.map((s) => (String(s.id).startsWith('custom_') ? s.name : (Number(s.id) || s.name))),
          qualification,
          gender_preference: genderPreference && genderPreference !== '' ? genderPreference : 'Any',
          language_requirements: languageRequirements,
          willingness_to_relocate: willingnessToRelocate,
          age_min: ageMin !== '' ? Number(ageMin) : 18,
          age_max: ageMax !== '' ? Number(ageMax) : 75,
          is_closed: isClosed,
        };
        await apiService.addLookingForWithStep(payload, 'preferences');
        setCompletedSteps(prev => new Set(prev).add(1));
      }

      // ── STEP 3 → DESCRIPTION (run if not yet completed or currently on step 3) ──
      if (activeStep >= 2 && (!completedSteps.has(2) || activeStep === 2)) {
        const payload: any = {
          id: currentJobId,
          is_draft: true,
          description,
          responsibilities: responsibilities.filter(r => r.trim()),
          requirements: requirements.filter(r => r.trim()),
          benefits,
          about_company: aboutCompany,
          perks: perksSection,
          careers_page_link: careersPageLink,
          is_closed: isClosed,
        };
        await apiService.addLookingForWithStep(payload, 'description');
        setCompletedSteps(prev => new Set(prev).add(2));
      }

      // ── STEP 4 → COMMUNICATION ──
      if (activeStep === 3) {
        const payload: any = {
          id: currentJobId,
          is_draft: status === 'publish' ? false : true,
          is_closed: isClosed,
          notify_email: notifyEmail,
          notify_sms: notifySms,
          notify_whatsapp: notifyWhatsapp,
          notify_emails: notifyEmails,
          ack_template: ackTemplate,
        };
        await apiService.addLookingForWithStep(payload, 'communication');
        setCompletedSteps(prev => new Set(prev).add(3));
      }

      return true;
    } catch (error: any) {

      // Show the actual API error message — not a generic one
      const apiMessage = error?.response?.data?.message || error?.message;
      if (apiMessage && apiMessage !== 'Please fix the errors before proceeding') {
        toast.error(apiMessage);
      }
      // If the API says "fix errors", the field-level errors are already shown inline — no toast needed
      return false;
    }
  };

  // ── Save & Continue (advance to next step) ──
  const goNext = async (status?:string) => {
    if (isNavigating) return;
    setIsNavigating(true);
    try {
      const saved = await (status ? saveCurrentStep(status) : saveCurrentStep());
      if (!saved) return;
      // If final step (communication), close modal
      if (activeStep === 3) {
        notifyCreditChange(isEditingExistingJob ? 0 : 5);
        toast.success(
          isEditingExistingJob ? 'Job updated successfully 🚀' : 'Job created successfully 🚀'
        );
        onCancel();
        return;
      }

      // Otherwise advance to next step
      setActiveStep((prev) => (prev + 1) as StepIndex);
    } finally {
      setIsNavigating(false);
    }
  };

  // ── Save & Close (save current step then close modal) ──
  const handleSaveAndClose = async () => {
    if (isNavigating) return;
    setIsNavigating(true);
    setIsSavingDraft(true);
    try {
      const saved = await saveCurrentStep();
      if (saved) {
        toast.success(isEditingExistingJob ? 'Job post updated successfully' : 'Progress saved successfully');
        onCancel(); // TC2: close modal after save
      }
    } finally {
      setIsSavingDraft(false);
      setIsNavigating(false);
    }
  };

  const goBack = () => {
    if (activeStep > 0) {
      setDirection('backward');
      setActiveStep((activeStep - 1) as StepIndex);
    }
  };

  // ── Close with auto-draft ──

  const handleClose = () => {
    if (!persistedJobId && !jobDetails?.id && jobTitle.trim()) {
      setShowLeaveConfirm(true);
    } else {
      onCancel();
    }
  };

  const handleLeaveAndDraft = async () => {
    const payload: any = {
      designation_id: designationId,
      job_title: jobTitle,
      skills: selectedSkills.map((s) => (String(s.id).startsWith('custom_') ? s.name : (Number(s.id) || s.name))),
      employment_type_ids: employmentTypeIds,
      work_experience_years: Number(minExp) || 0,
      work_experience_months: 0,
      experience_id: experienceId,
      description,
      salary_min: salaryVisibility === 'range' && salaryMin !== '' ? Number(salaryMin) : null,
      salary_max: salaryVisibility === 'range' && salaryMax !== '' ? Number(salaryMax) : null,
      salary_currency: salaryCurrency,
      salary_period: salaryPeriod,
      salary_visibility: salaryVisibility,
      openings_count: openingsCount !== '' ? Number(openingsCount) : null,
      location_text: locationText,
      location_lat: locationLat,
      location_lng: locationLng,
      //       industry_tags: industryTags.map(name =>
      //   allIndustries.find(ind => ind.name === name)?.id
      // ).filter((id): id is number => id !== undefined),
      industry_tags: industryTags,
      // industry_tags: industryTags.map(name => allIndustries.find(ind => ind.name === name)?.id).filter((id): id is number => id !== undefined),
      responsibilities: responsibilities.filter(Boolean),
      requirements: requirements.filter(Boolean),
      benefits: null,
      is_closed: isClosed,
      is_draft: true,
      work_mode: workMode || undefined,
      application_deadline: applicationDeadline || null,
      language_requirements: languageRequirements.filter(Boolean),
      willingness_to_relocate: willingnessToRelocate,
      age_min: ageMin !== '' ? Number(ageMin) : null,
      age_max: ageMax !== '' ? Number(ageMax) : null,
      auto_reject_rules: autoRejectRules.filter(Boolean),
      about_company: aboutCompany,
      perks: perksSection.filter(Boolean),
      careers_page_link: careersPageLink,
      notify_emails: notifyEmails.filter(Boolean),
      ack_template: ackTemplate,
    };
    const draftJobId = jobDetails?.id ?? persistedJobId;
    if (draftJobId != null) {
      payload.id = String(draftJobId);
    }
    setIsSavingDraft(true);
    try {
      const res = await handleAddJob(payload);
      if (res?.status) {
        toast.success('Draft saved automatically');
        onAutoDraft?.(payload);
      }
    } catch { /* silent */ }
    finally { setIsSavingDraft(false); }
    setShowLeaveConfirm(false);
    onCancel();
  };

  const handleLeaveWithoutSaving = () => {
    setShowLeaveConfirm(false);
    onCancel();
  };

  // ── Final submit (Save Changes / Post Job on last step) ──
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const handleFinalSubmit = async () => {
    if (!isDomainVerified) {
      setIsSubmittingBtn(true);
      try {
        const saved = await saveCurrentStep('draft');
        if (saved) {
          toast.info('Job saved as draft! Domain verification is required to publish.');
          setShowVerificationModal(true);
        }
      } finally {
        setIsSubmittingBtn(false);
      }
      return;
    }

    const isPaidJob = Boolean(jobDetails?.is_paid || (jobDetails?.id && !jobDetails?.is_draft));
    if (!isPaidJob) {
      const confirmed = await requestConfirm({
        moduleKey: 'post_job',
        moduleName: 'Post Job',
        featureKey: 'JOB_POST',
        creditCost: 5,
        actionDescription: 'Posting this job position will deduct 5 credits from your balance.',
      });
      if (!confirmed) return;
    }

    setIsSubmittingBtn(true);
    try {
      await goNext('publish'); // goNext calls saveCurrentStep() for the communication step then closes
    } finally {
      setIsSubmittingBtn(false);
    }
  };

  // ── Save as Draft ──

  const handleSaveDraft = async () => {
    if (!jobTitle.trim()) { toast.error('Please enter a job title to save as draft'); return; }
    setIsSavingDraft(true);
    const payload: NewJobPayload = {
      designation_id: designationId,
      job_title: jobTitle,
      skills: selectedSkills.map((s) => (String(s.id).startsWith('custom_') ? s.name : (Number(s.id) || s.name))),
      employment_type_ids: employmentTypeIds,
      work_experience_years: Number(minExp) || 0,
      work_experience_months: 0,
      experience_id: experienceId,
      description,
      salary_min: salaryVisibility === 'range' && salaryMin !== '' ? Number(salaryMin) : null,
      salary_max: salaryVisibility === 'range' && salaryMax !== '' ? Number(salaryMax) : null,
      salary_currency: salaryCurrency,
      salary_period: salaryPeriod,
      openings_count: openingsCount !== '' ? Number(openingsCount) : null,
      location_text: locationText,
      location_lat: locationLat,
      location_lng: locationLng,
      industry_tags: industryTags,
      // industry_tags: industryTags.map(name => allIndustries.find(ind => ind.name === name)?.id).filter((id): id is number => id !== undefined),
      responsibilities: responsibilities.filter(Boolean),
      requirements: requirements.filter(Boolean),
      benefits: null,
      is_closed: isClosed,
      ...((jobDetails?.id ?? persistedJobId) != null ? { id: String(jobDetails?.id ?? persistedJobId) } : {}),
      work_mode: workMode || undefined,
      application_deadline: applicationDeadline || null,
      language_requirements: languageRequirements.filter(Boolean),
      willingness_to_relocate: willingnessToRelocate,
      age_min: ageMin !== '' ? Number(ageMin) : null,
      age_max: ageMax !== '' ? Number(ageMax) : null,
      auto_reject_rules: autoRejectRules.filter(Boolean),
      about_company: aboutCompany,
      perks: perksSection.filter(Boolean),
      careers_page_link: careersPageLink,
      notify_emails: notifyEmails.filter(Boolean),
      ack_template: ackTemplate,
    } as any;
    (payload as any).is_draft = true;
    try {
      const res = await handleAddJob(payload);
      if (res?.status) {
        toast.success('Draft saved successfully');
        handleClose();
      } else {
        toast.error(res?.message || 'Failed to save draft');
      }
    } finally { setIsSavingDraft(false); }
  };

  // ── Skills helper ──

  const addSuggestedSkill = (skill: SkillsListArray) => {
    if (!selectedSkills.some((s) => s.id === skill.id)) {
      setSelectedSkills((prev) => [...prev, skill]);
      setStep2Errors((prev) => ({ ...prev, skills: '' }));
    }
  };

  const handleJobLocationChange = (address: string, placeDetails?: any) => {
    // Always update — credit warning is shown separately on first focus
    setLocationText(address);
    setIsDirty(true);
    setLocationValidated(!!placeDetails?.geometry);

    if (placeDetails?.geometry?.location) {
      setLocationLat(placeDetails.geometry.location.lat());
      setLocationLng(placeDetails.geometry.location.lng());
    } else {
      setLocationLat(null);
      setLocationLng(null);
    }
  };

  const handleUseCurrentJobLocation = () => {
    if (!hasConfirmedJobLocationEdit) {
      // setShowJobLocationCreditWarning(true);
      return;
    }

    if (!(window as any).google?.maps?.Geocoder) {
      toast.info('Google Places is still loading. Please try again in a moment.');
      return;
    }

    setUseCurrentJobLocation(false);
    window.setTimeout(() => setUseCurrentJobLocation(true), 0);
  };

  const handleUseCompanyLocation = () => {
    if (companyLocation && companyLocation.text) {
      if (jobDetails?.id && !hasConfirmedJobLocationEdit) {
        setPendingLocationChange({
          address: companyLocation.text,
          placeDetails: {
            geometry: companyLocation.lat != null && companyLocation.lng != null ? {
              location: {
                lat: () => companyLocation.lat!,
                lng: () => companyLocation.lng!
              }
            } : null
          }
        });
        // setShowJobLocationCreditWarning(true);
      } else {
        setLocationText(companyLocation.text);
        setIsDirty(true);
        setLocationValidated(true);
        setLocationLat(companyLocation.lat);
        setLocationLng(companyLocation.lng);
        setStep1Errors((prev) => ({
          ...prev,
          location: "",
        }));
      }
    } else {
      toast.info('No company location set in profile.');
    }
  };

  const handleConfirmJobLocationEdit = () => {
    setHasConfirmedJobLocationEdit(true);
    // setShowJobLocationCreditWarning(false);
    // Apply pending location if any
    if (pendingLocationChange) {
      const { address, placeDetails } = pendingLocationChange;
      setLocationText(address);
      setIsDirty(true);
      setLocationValidated(!!placeDetails?.geometry);
      if (placeDetails?.geometry?.location) {
        setLocationLat(placeDetails.geometry.location.lat());
        setLocationLng(placeDetails.geometry.location.lng());
      } else {
        setLocationLat(null);
        setLocationLng(null);
      }
      setPendingLocationChange(null);
    }
  };

  const handleJobLocationEditAttempt = (event: React.SyntheticEvent) => {
    if (hasConfirmedJobLocationEdit || !jobDetails?.id) return;
    event.preventDefault();
    // setShowJobLocationCreditWarning(true);
  };

  // ── Render helpers ──

  const renderStepIndicator = () => (
    <div className="ajm-step-indicator">
      <div className="ajm-step-progress-bar">
        <div
          className="ajm-step-progress-fill"
          style={{ width: `${((activeStep) / (STEPS.length - 1)) * 100}%` }}
        />
      </div>
      <div className="ajm-step-list">
        {STEPS.map((step, idx) => {
          const isDone = completedSteps.has(idx);
          const isActive = activeStep === idx;
          const isClickable = canAccessStep(idx);
          const Icon = step.icon;

          return (
            <div
              key={step.id}
              className={`ajm-step-item ${isActive ? 'ajm-step-item--active' : ''} ${isDone ? 'ajm-step-item--done' : ''} ${isClickable ? 'ajm-step-item--clickable' : ''}`}
              onClick={() => handleStepClick(idx)}
            >
              <div className="ajm-step-bubble">
                {isDone ? <Check size={14} /> : <Icon size={14} />}
              </div>
              <div className="ajm-step-info">
                <div className="ajm-step-title">{step.label}</div>
                <div className="ajm-step-desc">{step.description}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Step 1: Job Basics ──

  const selectedExperienceLabel =
    allWorkExperienceList.find((item) => String(item.id) === String(experienceId))?.name || '';
  const selectedWorkModeLabel = workModeOptions.find((mode) => mode.value === workMode)?.label || workMode;
  const selectedSalaryVisibilityLabel =
    salaryVisibilityOptions.find((option) => option.value === salaryVisibility)?.label || salaryVisibility;

  const renderStep1 = () => (
    <div className="ajm-step-content">
      <div className="ajm-step-header">
        <h2 className="ajm-step-title-main">Job Basics</h2>
        <p className="ajm-step-subtitle">Start with the essential details about the position</p>
      </div>

      <div className="ajm-section-card">
        <div className="ajm-section-card__header">
          <h3 className="ajm-section-card__title">Role Details</h3>
          <p className="ajm-section-card__subtitle">Define the position and hiring basics</p>
        </div>
        <div className="ajm-inline-grid">
          {/* Job Title with Autocomplete */}
          <div className="ajm-field-group" ref={jobTitleFieldRef}>
            <label className="ajm-section-label">Job Title <span className="ajm-required">*</span></label>
            {isEditingExistingJob && jobTitle && !openDesignationList ? (
              /* ── Edit/Duplicate mode: show saved title as a locked display, click to change ── */
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 14px",
                  border: "1.5px solid #E2E8F0",
                  borderRadius: 10,
                  background: "#F8FAFC",
                  cursor: "default",
                }}
              >
                <Briefcase size={16} style={{ color: "#94A3B8", flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: "0.9rem", fontWeight: 600, color: "#1E293B" }}>
                  {jobTitle}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setOpenDesignationList(true);
                    getDesignation(jobTitle);
                  }}
                  style={{
                    border: "none",
                    background: "none",
                    color: "#457EFF",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    padding: "2px 8px",
                    borderRadius: 6,
                    whiteSpace: "nowrap",
                  }}
                >
                  Change
                </button>
              </div>
            ) : (
              <FloatingLabelInput
                label=""
                value={jobTitle}
                onChange={(val) => {
                  setJobTitle(val);
                  setStep1Errors(prev => ({ ...prev, jobTitle: '' }));
                  if (val.trim().length > 0) {
                    setOpenDesignationList(true);
                    getDesignation(val);
                  } else {
                    setOpenDesignationList(false);
                  }
                }}
                placeholder="e.g. Senior Frontend Developer"
                error={step1Errors.jobTitle && !jobTitle.trim() ? step1Errors.jobTitle : ''}
                icon={<Briefcase size={16} />}
              />
            )}


            {openDesignationList && jobTitle && (
              <div className="ajm-autocomplete-dropdown">
                {isLoadingDesignations ? (
                  <div className="ajm-dropdown-loading">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="ajm-dropdown-skeleton">
                        <SkeletonLoader width="80%" height={16} />
                      </div>
                    ))}
                  </div>
                ) : allDesignationList.length > 0 ? (
                  allDesignationList.map((item) => (
                    <button
                      key={item.id}
                      className="ajm-dropdown-item"
                      onClick={() => {
                        setDesignationId(item.id);
                        setJobTitle(item.name);
                        setStep1Errors(prev => ({ ...prev, jobTitle: '' }));
                        setOpenDesignationList(false);
                      }}
                    >
                      <Sparkles size={14} className="ajm-dropdown-icon" />
                      <span>{item.name}</span>
                    </button>
                  ))
                ) : (
                  <div className="ajm-dropdown-empty">No matching positions found</div>
                )}
              </div>
            )}
          </div>

          {/* Employment Type */}
          <div className="ajm-field-group">
            <label className="ajm-section-label">Employment Type <span className="ajm-required">*</span></label>
            <div className={step1Errors.employmentType ? 'ajm-control-shell ajm-control-shell--error' : ''}>
              <Select
                value={employmentTypeIds[0] ? { value: String(employmentTypeIds[0]), label: employmentTypeOptions.find(t => String(t.value) === String(employmentTypeIds[0]))?.label || '' } : null}
                onChange={(opt: any) => {
                  if (!opt) {
                    setEmploymentTypeIds([]);
                  } else {
                    const num = Number(opt.value);
                    setEmploymentTypeIds(num > 0 ? [num] : []);
                  }
                  setStep1Errors(prev => ({ ...prev, employmentType: '' }));
                }}
                options={employmentTypeOptions.map((t) => ({ value: String(t.value), label: t.label })) as any}
                placeholder="Select employment type"
                classNamePrefix="ajm-select"
                styles={selectStyles(!!step1Errors.employmentType)}
                isClearable
              />
            </div>
            {step1Errors.employmentType && <span className="ajm-error-text">{step1Errors.employmentType}</span>}
          </div>
        </div>

        {/* Industry */}
        <div className="ajm-field-group">
          <label className="ajm-section-label">Industry <span className="ajm-required">*</span></label>
          <div className={step1Errors.industry ? 'ajm-control-shell ajm-control-shell--error' : ''}>
            <IndustryMultiSelect
              selected={industryTags}
              onChange={(val) => {
                setIndustryTags(val);
                setStep1Errors(prev => ({ ...prev, industry: '' }));
              }}
              allIndustries={allIndustries}
            />
          </div>
          {step1Errors.industry && <span className="ajm-error-text">{step1Errors.industry}</span>}
        </div>

        <div className="ajm-inline-grid">
          {/* Work Mode */}
          <div className="ajm-field-group">
            <label className="ajm-section-label">Work Mode <span className="ajm-required">*</span></label>
            <div className={step1Errors.workMode ? 'ajm-control-shell ajm-control-shell--error' : ''}>
              <Select
                value={workMode ? { value: workMode, label: workModeOptions.find(m => m.value === workMode)?.label || '' } : null}
                onChange={(opt: any) => {
                  setWorkMode((opt?.value || '') as typeof workMode);
                  setStep1Errors(prev => ({ ...prev, workMode: '' }));
                }}
                options={workModeOptions.map((m) => ({ value: m.value, label: m.label })) as any}
                placeholder="Select work mode"
                classNamePrefix="ajm-select"
                styles={selectStyles(!!step1Errors.workMode)}
                isClearable
              />
            </div>
            {step1Errors.workMode && <span className="ajm-error-text">{step1Errors.workMode}</span>}
          </div>

          {/* Application Deadline */}
          <div className="ajm-field-group">
            <label className="ajm-section-label">Application Deadline <span className="ajm-optional">(Optional)</span></label>
            <input
              type="date"
              value={applicationDeadline}
              min={isClosed ? undefined : new Date().toISOString().split('T')[0]}
              onChange={(e) => {
                setApplicationDeadline(e.target.value);
                setStep1Errors(prev => ({ ...prev, applicationDeadline: '' }));
                setReopenDeadlineError(false);
              }}
              className={`ajm-range-select${step1Errors.applicationDeadline ? ' ajm-range-select--error' : ''}`}
              style={step1Errors.applicationDeadline ? { borderColor: '#ef4444' } : undefined}
            />
            {step1Errors.applicationDeadline && (
              <span className="ajm-error-text">{step1Errors.applicationDeadline}</span>
            )}
            {isClosed && applicationDeadline && (() => {
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const dl = new Date(applicationDeadline);
              return dl < today ? (
                <span className="ajm-helper-text" style={{ color: '#f59e0b', fontSize: '0.78rem', marginTop: 4, display: 'block' }}>
                  ⚠ Deadline is in the past. Update or clear it to reopen this job.
                </span>
              ) : null;
            })()}
          </div>
        </div>

        <div className="ajm-inline-grid">
          {/* Experience */}
          <div className="ajm-field-group">
            <label className="ajm-section-label">Experience Required <span className="ajm-required">*</span></label>
            <div className={step1Errors.experience ? 'ajm-control-shell ajm-control-shell--error' : ''}>
              <Select
                value={experienceId ? { value: String(experienceId), label: allWorkExperienceList.find(item => String(item.id) === String(experienceId))?.name || '' } : null}
                onChange={(opt: any) => {
                  const selectedId = opt ? opt.value : '';
                  setExperienceId(selectedId);
                  setStep1Errors(prev => ({ ...prev, experience: '' }));
                  const selected = allWorkExperienceList.find((item) => String(item.id) === String(selectedId));
                  const firstNumber = Number(String(selected?.name || '').match(/\d+/)?.[0] || 0);
                  setMinExp(firstNumber ? String(firstNumber) : '');
                }}
                options={allWorkExperienceList.map((item) => ({ value: String(item.id), label: item.name })) as any}
                placeholder="Select experience"
                classNamePrefix="ajm-select"
                styles={selectStyles(!!step1Errors.experience)}
                isClearable
              />
            </div>
            {step1Errors.experience && <span className="ajm-error-text">{step1Errors.experience}</span>}
            {/* {step1Errors.exp && <span className="ajm-error-text">{step1Errors.exp}</span>} */}
          </div>

          {/* Openings */}
          <div className="ajm-field-group">
            <label className="ajm-section-label">Number of Openings <span className="ajm-optional">(Optional)</span></label>
            <div className="ajm-openings-stepper">
              <button
                type="button"
                className="ajm-openings-btn"
                onClick={() => {
                  const current = openingsCount === '' ? 0 : Number(openingsCount);
                  setOpeningsCount(Math.max(0, current - 1));
                }}
                aria-label="Decrease openings"
              >
                <Minus size={14} />
              </button>
              <input
                type="number"
                min={0}
                max="99999"
                value={openingsCount}
                className="ajm-openings-input"
                placeholder="0"
                onChange={(e) => {
                  const raw = e.target.value;

                  if (raw === '') {
                    setOpeningsCount('');
                    return;
                  }

                  if (raw.length > 5) {
                    return;
                  }

                  const parsed = Number(raw);

                  setOpeningsCount(
                    Number.isNaN(parsed) ? '' : Math.max(0, parsed)
                  );
                }}
              />
              <button
                type="button"
                className="ajm-openings-btn"
                onClick={() => {
                  const current = openingsCount === '' ? 0 : Number(openingsCount);
                  setOpeningsCount(Math.max(0, current + 1));
                }}
                aria-label="Increase openings"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="ajm-section-card">
        <div className="ajm-section-card__header">
          <h3 className="ajm-section-card__title">Salary <span className="ajm-required">*</span></h3>
          <p className="ajm-section-card__subtitle">Add salary details</p>
        </div>
        {/* Salary */}
        <div className="ajm-field-group">
          {/* <label className="ajm-section-label">Salary <span className="ajm-required">*</span></label> */}

          {/* TC3/TC4/TC5: "Enter Range" pill toggles the inputs; "Save Range" button is inside the row */}
          {salaryVisibility !== 'range' ? (
            <div className="ajm-salary-options">
              <button
                type="button"
                className="ajm-salary-opt-btn"
                onClick={() => {
                  setSalaryVisibility('range');
                  setStep1Errors(prev => ({ ...prev, salaryMin: '', salaryMax: '', salary: '' }));
                }}
              >
                Enter Range
              </button>
            </div>
          ) : (
            /* Number inputs row — shown when range is selected */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="ajm-salary-inputs">
                <CurrencySelect
                  options={currencyOptions}
                  value={salaryCurrency}
                  onChange={setSalaryCurrency}
                />
                {/* Min salary must be greater than 0 */}
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Min (e.g. 100)"
                  value={salaryMin}
                  onChange={(e) => {
                    const { maxDigits } = getMaxSalaryForPeriod(salaryPeriod);
                    const raw = e.target.value.replace(/[^0-9]/g, ''); // digits only
                    if (raw.length > maxDigits) return; // block > maxDigits
                    const val = raw === '' ? '' : Number(raw);
                    setSalaryMin(val);
                    setStep1Errors(prev => ({ ...prev, salaryMin: '', salary: '' }));
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const { maxDigits } = getMaxSalaryForPeriod(salaryPeriod);
                    const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, maxDigits);
                    const val = pasted === '' ? '' : Number(pasted);
                    setSalaryMin(val);
                    setStep1Errors(prev => ({ ...prev, salaryMin: '', salary: '' }));
                  }}
                  onBlur={() => {
                    const { max: maxLimit, label: maxLabel } = getMaxSalaryForPeriod(salaryPeriod);
                    if (salaryMin !== '' && Number(salaryMin) <= 0) {
                      setStep1Errors(prev => ({ ...prev, salaryMin: 'Minimum salary must be greater than 0' }));
                    } else if (salaryMin !== '' && Number(salaryMin) > maxLimit) {
                      setStep1Errors(prev => ({ ...prev, salaryMin: `Minimum salary cannot exceed ${maxLabel}` }));
                    }
                  }}
                  className={`ajm-salary-input${step1Errors.salaryMin ? ' ajm-salary-input--error' : ''}`}
                />
                <span className="ajm-salary-separator">to</span>
                {/* Max salary based on period: 12 Cr/yr, 1 Cr/mo, 10k/hr */}
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder={`Max (up to ${getMaxSalaryForPeriod(salaryPeriod).shortLabel})`}
                  value={salaryMax}
                  onChange={(e) => {
                    const { maxDigits } = getMaxSalaryForPeriod(salaryPeriod);
                    const raw = e.target.value.replace(/[^0-9]/g, ''); // digits only
                    if (raw.length > maxDigits) return; // block > maxDigits
                    const val = raw === '' ? '' : Number(raw);
                    setSalaryMax(val);
                    setStep1Errors(prev => ({ ...prev, salaryMax: '', salary: '' }));
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const { maxDigits } = getMaxSalaryForPeriod(salaryPeriod);
                    const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, maxDigits);
                    const val = pasted === '' ? '' : Number(pasted);
                    setSalaryMax(val);
                    setStep1Errors(prev => ({ ...prev, salaryMax: '', salary: '' }));
                  }}
                  onBlur={() => {
                    const { max: maxLimit, label: maxLabel } = getMaxSalaryForPeriod(salaryPeriod);
                    if (salaryMax !== '' && Number(salaryMax) > maxLimit) {
                      setStep1Errors(prev => ({ ...prev, salaryMax: `Maximum salary cannot exceed ${maxLabel}` }));
                    } else if (salaryMin !== '' && salaryMax !== '' && Number(salaryMin) > 0 && Number(salaryMax) <= Number(salaryMin)) {
                      setStep1Errors(prev => ({ ...prev, salary: 'Maximum salary must be greater than minimum salary' }));
                    }
                  }}
                  className={`ajm-salary-input${step1Errors.salaryMax ? ' ajm-salary-input--error' : ''}`}
                />
                <select
                  value={salaryPeriod}
                  onChange={(e) => {
                    const newPeriod = e.target.value;
                    setSalaryPeriod(newPeriod);
                    const { max: maxLimit, label: maxLabel } = getMaxSalaryForPeriod(newPeriod);
                    if (salaryMax !== '' && Number(salaryMax) > maxLimit) {
                      setStep1Errors(prev => ({ ...prev, salaryMax: `Maximum salary cannot exceed ${maxLabel}` }));
                    } else if (salaryMin !== '' && Number(salaryMin) > maxLimit) {
                      setStep1Errors(prev => ({ ...prev, salaryMin: `Minimum salary cannot exceed ${maxLabel}` }));
                    } else if (salaryMin !== '' && salaryMax !== '' && Number(salaryMin) > 0 && Number(salaryMax) <= Number(salaryMin)) {
                      setStep1Errors(prev => ({ ...prev, salary: 'Maximum salary must be greater than minimum salary' }));
                    } else {
                      setStep1Errors(prev => ({ ...prev, salaryMax: '', salaryMin: '', salary: '' }));
                    }
                  }}
                  className="ajm-period-select"
                >
                  {salaryPeriodOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                {/* TC4/TC5: "Save Range" button inline at end of row */}
                {/* <button
                  type="button"
                  className="ajm-salary-opt-btn ajm-salary-opt-btn--active"
                  style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  onClick={() => {
                    // Validate inline before saving
                    const errs: Record<string, string> = {};
                    if (salaryMin === '' || Number(salaryMin) <= 0) errs.salaryMin = 'Min must be greater than 0';
                    if (salaryMax === '' || Number(salaryMax) > 9999999) errs.salaryMax = 'Max must be ≤ 9,999,999';
                    if (!errs.salaryMin && !errs.salaryMax && Number(salaryMax) <= Number(salaryMin)) errs.salary = 'Max must be greater than Min';
                    if (Object.keys(errs).length > 0) {
                      setStep1Errors(prev => ({ ...prev, ...errs }));
                    } else {
                      setStep1Errors(prev => ({ ...prev, salaryMin: '', salaryMax: '', salary: '' }));
                    }
                  }}
                >
                  Save Range
                </button> */}
              </div>
            </div>
          )}
          {step1Errors.salaryMin && <span className="ajm-error-text">{step1Errors.salaryMin}</span>}
          {step1Errors.salaryMax && !step1Errors.salaryMin && <span className="ajm-error-text">{step1Errors.salaryMax}</span>}
          {step1Errors.salary && !step1Errors.salaryMin && !step1Errors.salaryMax && <span className="ajm-error-text">{step1Errors.salary}</span>}
        </div>
      </div>

      <div className="ajm-section-card">
        <div className="ajm-section-card__header d-flex justify-content-between gap-1">
          <div>
            <h3 className="ajm-section-card__title">Job Location <span className="ajm-required">*</span></h3>
          <p className="ajm-section-card__subtitle">Help candidates understand where to join</p>
          </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {process.env.REACT_APP_FIREBASE_ENV !== "staging" && (
                <button
                  type="button"
                  className="ajm-location-current-btn"
                  onClick={handleUseCurrentJobLocation}
                >
                  Use My Location
                </button>
              )}
              {companyLocation && companyLocation.text && (
                <>
                  {process.env.REACT_APP_FIREBASE_ENV !== "staging" && (
                    <span style={{ color: 'var(--ajm-gray-300)', fontSize: '0.8rem' }}>|</span>
                  )}
                  <button
                    type="button"
                    className="ajm-location-current-btn"
                    onClick={handleUseCompanyLocation}
                  >
                    Use Company Location
                  </button>
                </>
              )}
            </div>
        </div>
        {/* Location */}
        
        <div className="ajm-field-group">
          {/* <div className="ajm-location-label-row">
            <label className="ajm-section-label" htmlFor="job_location_text"></label>
          </div> */}
          
          <div
            className={step1Errors.location ? 'ajm-control-shell ajm-control-shell--error' : ''}
            onClick={() => {
              // Show credit warning once on first interaction in edit mode
              if (jobDetails?.id && !hasConfirmedJobLocationEdit) {
                // setShowJobLocationCreditWarning(true);
              }
            }}
          >
           {
  process.env.REACT_APP_FIREBASE_ENV === "staging" ? (
    <FallbackPlacesAutocomplete
      className="ajm-google-places-input"
      value={locationText}
      onChange={(val: string, placeDetails?: any) => {
        if (jobDetails?.id && !hasConfirmedJobLocationEdit) return;

        handleJobLocationChange(val, placeDetails);

        setStep1Errors((prev) => ({
          ...prev,
          location: "",
        }));
      }}
      placeholder="Search and select location"
      id="job_location_text"
      name="location_text"
    />
  ) : (
    <GooglePlacesAutocomplete
      className="ajm-google-places-input"
      value={locationText}
      onChange={(val: string, placeDetails?: any) => {
        if (jobDetails?.id && !hasConfirmedJobLocationEdit) return;

        handleJobLocationChange(val, placeDetails);

        setStep1Errors((prev) => ({
          ...prev,
          location: "",
        }));
      }}
      placeholder="Search and select location"
      id="job_location_text"
      name="location_text"
      showMapButton={false}
      currentLocation={useCurrentJobLocation}
    />
  )
}
          </div>
          {step1Errors.location && <span className="ajm-error-text">{step1Errors.location}</span>}
          {!step1Errors.location && locationText && !locationValidated && (
            <span className="ajm-error-text" style={{ color: '#d68600' }}>
              Please select a location from the dropdown suggestions
            </span>
          )}
        </div>

        {/* Perks */}

        {/* <div className="ajm-field-group">
          <label className="ajm-section-label">Perks & Benefits <span className="ajm-optional">(Optional)</span></label>
          <div className="ajm-perks-grid">
            {perkSuggestions.map((perk) => (
              <button
                key={perk}
                type="button"
                className={`ajm-perk-btn ${selectedPerks.includes(perk) ? 'ajm-perk-btn--active' : ''}`}
                onClick={() => {
                  setSelectedPerks(prev =>
                    prev.includes(perk) ? prev.filter(p => p !== perk) : [...prev, perk]
                  );
                }}
              >
                {selectedPerks.includes(perk) ? <Check size={12} /> : <Plus size={12} />}
                {perk}
              </button>
            ))}
          </div>
        </div> */}


      </div>
    </div>
  );

  // ── Step 2: Candidate Preferences ──
  const paginatedSkills = suggestedSkills; // API already returns the current page



  const renderStep2 = () => (
    <div className="ajm-step-content">
      <div className="ajm-step-header">
        <h2 className="ajm-step-title-main">Candidate Preferences</h2>
        <p className="ajm-step-subtitle">Define your ideal candidate profile</p>
      </div>

      {/* Department */}
      {/* <div className="ajm-field-group">
        <label className="ajm-section-label">Department</label>
        <CustomSingleSelect
          value={department}
          onChange={setDepartment}
          options={departmentOptions}
          placeholder="Select Department"
        />
      </div> */}

      {/* Skills */}
      <div className="ajm-field-group">
        <label className="ajm-section-label">Required Skills <span className="ajm-required">*</span></label>
        <div className={step2Errors.skills ? 'ajm-control-shell ajm-control-shell--error' : 'ajm-control-shell'}>
          <SkillsMultiSelect
            selected={selectedSkills}
            onChange={(vals) => {
              setSelectedSkills(vals);
              setStep2Errors((prev) => ({ ...prev, skills: '' }));
            }}
            allSkills={allSkillsList}
            isLoading={isLoadingSkills}
            onSearch={(v) => setSearchSkill(v)}
          />
        </div>
        {step2Errors.skills && <span className="ajm-error-text">{step2Errors.skills}</span>}




        {(suggestedSkills.length > 0 || isLoadingSkillsByIndustries) && (
          <div className="ajm-suggested-skills">

            <div className="ajm-suggested-header">
              <Sparkles size={14} />
              <span>Suggested based on your industry</span>
            </div>

            {/* Chips */}
            <div className="ajm-suggested-skills-container_box">
              {isLoadingSkillsByIndustries ? (
                <div className="ajm-suggested-loading">Loading skills...</div>
              ) : (
                <div className="ajm-suggested-list">
                  {paginatedSkills.map((skill) => {
                    const isSelected = selectedSkills.some((s) => s.id === skill.id);

                    return (
                      <button
                        key={skill.id}
                        type="button"
                        disabled={isSelected}
                        className={`ajm-suggested-chip ${isSelected ? "ajm-suggested-chip--selected" : ""
                          }`}
                        onClick={() => addSuggestedSkill(skill)}
                      >
                        {!isSelected && <Plus size={10} />}
                        {skill.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ✅ Pagination (styled like Continue button) */}
            <div className="ajm-pagination">

              <button
                type="button"
                className="ajm-btn ajm-btn--secondary"
                disabled={suggestedPage === 1 || isLoadingSkillsByIndustries}
                onClick={() => setSuggestedPage((prev) => prev - 1)}
              >
                Prev
              </button>

              <span className="ajm-pagination-info">{suggestedPage} / {suggestedTotalPages}</span>

              <button
                type="button"
                className="ajm-btn ajm-btn--primary"
                disabled={suggestedPage >= suggestedTotalPages || isLoadingSkillsByIndustries}
                onClick={() => setSuggestedPage((prev) => prev + 1)}
              >
                Next
                <ChevronRight size={16} />
              </button>

            </div>

          </div>
        )}
      </div>


      {/* Qualification */}
      <div className="ajm-field-group">
        <label className="ajm-section-label">Minimum Qualification <span className="ajm-required">*</span></label>
        <div className={step2Errors.qualification ? 'ajm-control-shell ajm-control-shell--error' : 'ajm-control-shell'}>
          <CustomMultiSelect
            selected={Array.isArray(qualification) ? qualification : (typeof qualification === 'string' ? [qualification] : [])}
            onChange={(val) => {
              setQualification(val);
              setStep2Errors(prev => ({ ...prev, qualification: '' }));
            }}
            options={qualificationOptions.map(q => q.value)}
            placeholder="Select qualifications..."
          />
        </div>
        {step2Errors.qualification && <span className="ajm-error-text">{step2Errors.qualification}</span>}
      </div>

      {/* Gender Preference */}
      <div className="ajm-field-group">
        <label className="ajm-section-label">Gender Preference</label>
        <div className="ajm-gender-selector">
          {genderOptions.map((g) => (
            <button
              key={g.value}
              type="button"
              className={`ajm-gender-btn ${genderPreference === g.value ? 'ajm-gender-btn--active' : ''}`}
              onClick={() => setGenderPreference(g.value)}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {/* Candidate Industries */}
      {/* <div className="ajm-field-group">
        <label className="ajm-section-label">Preferred Industry Background</label>
        <IndustryMultiSelect
          selected={candidateIndustries}
          onChange={setCandidateIndustries}
          allIndustries={allIndustries}
        />
      </div> */}

      {/* Language Requirements */}
      <div className="ajm-field-group">
        <label className="ajm-section-label">Language Requirements <span className="ajm-required">*</span></label>
        <div className={step2Errors.language ? 'ajm-control-shell ajm-control-shell--error' : 'ajm-control-shell'}>
        <CustomMultiSelect
          selected={languageRequirements}
          onChange={(vals) => {setLanguageRequirements(vals.length === 0 ? [] : vals);
              setStep2Errors(prev => ({ ...prev, language: '' }))}

          }
          placeholder="Select languages..."
          options={[
            'English', 'Hindi', 'Spanish', 'French', 'German', 'Mandarin Chinese',
            'Arabic', 'Portuguese', 'Russian', 'Japanese', 'Korean', 'Italian',
            'Dutch', 'Turkish', 'Polish', 'Swedish', 'Norwegian', 'Danish',
            'Finnish', 'Greek', 'Hebrew', 'Thai', 'Vietnamese', 'Indonesian',
            'Malay', 'Bengali', 'Urdu', 'Tamil', 'Telugu', 'Marathi',
            'Gujarati', 'Kannada', 'Malayalam', 'Punjabi',
          ]}
        />
        </div>
        {step2Errors.language && <span className="ajm-error-text">{step2Errors.language}</span>}

      </div>

      {/* Willingness to Relocate */}
      {/* <div className="ajm-field-group">
        <label className="ajm-section-label">Willingness to Relocate</label>
        <select
          value={willingnessToRelocate}
          onChange={(e) => setWillingnessToRelocate(e.target.value as typeof willingnessToRelocate)}
          className="ajm-range-select"
        >
          {relocateOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div> */}

      {/* Age Range */}
      <div className="ajm-field-group">
        <label className="ajm-section-label">
          Age Range <span className="ajm-optional">(Optional — where legally permitted)</span>
          {(ageMin !== '' || ageMax !== '') && (
            <span className="ajm-range-value-badge">{ageMin !== '' ? ageMin : 18} yrs – {ageMax !== '' ? ageMax : 75} yrs</span>
          )}
        </label>
        <div className="ajm-dual-range">
          <div className="ajm-dual-range__track">
            <div
              className="ajm-dual-range__fill"
              style={{
                left: `${(((ageMin !== '' ? Number(ageMin) : 18) - 18) / (75 - 18)) * 100}%`,
                right: `${100 - (((ageMax !== '' ? Number(ageMax) : 75) - 18) / (75 - 18)) * 100}%`,
              }}
            />
          </div>
          <input
            type="range" min={18} max={75}
            value={ageMin !== '' ? Number(ageMin) : 18}
            onChange={(e) => {
              const val = Number(e.target.value);
              const max = ageMax !== '' ? Number(ageMax) : 75;
              setAgeMin(val >= max ? max - 1 : val);
            }}
            className="ajm-dual-range__input ajm-dual-range__input--min"
          />
          <input
            type="range" min={18} max={75}
            value={ageMax !== '' ? Number(ageMax) : 75}
            onChange={(e) => {
              const val = Number(e.target.value);
              const min = ageMin !== '' ? Number(ageMin) : 18;
              setAgeMax(val <= min ? min + 1 : val);
            }}
            className="ajm-dual-range__input ajm-dual-range__input--max"
          />
          <div className="ajm-dual-range__labels">
            <span>18 years</span>
            <span>75 years</span>
          </div>
        </div>
      </div>

    </div>
  );

  // ── Step 3: Screening Questions ──

  const updateQuestion = (index: number, patch: Partial<ScreeningQuestion>) => {
    setScreeningQuestions(prev => prev.map((q, i) => i === index ? { ...q, ...patch } : q));
  };

  const addOption = (index: number, opt: string) => {
    const q = screeningQuestions[index];
    const opts = q.options ?? [];
    if (opt.trim() && !opts.includes(opt.trim())) {
      updateQuestion(index, { options: [...opts, opt.trim()] });
    }
  };

  const removeOption = (qIndex: number, optIndex: number) => {
    const opts = [...(screeningQuestions[qIndex].options ?? [])];
    opts.splice(optIndex, 1);
    updateQuestion(qIndex, { options: opts });
  };

  const renderQuestionCard = (q: ScreeningQuestion, index: number) => (
    <QuestionCard
      key={q.id}
      q={q}
      index={index}
      onUpdate={(patch) => updateQuestion(index, patch)}
      onRemove={() => setScreeningQuestions(prev => prev.filter((_, i) => i !== index))}
      onAddOption={(opt) => addOption(index, opt)}
      onRemoveOption={(optIdx) => removeOption(index, optIdx)}
    />
  );

  /*
  const renderStep3 = () => (
    <div className="ajm-step-content">
      <div className="ajm-step-header">
        <h2 className="ajm-step-title-main">Screening Questions</h2>
        <p className="ajm-step-subtitle">Toggle the questions you want to include for candidates</p>
      </div>

      <div className="ajm-questions-list">
        {screeningQuestions.map((q, index) => renderQuestionCard(q, index))}
      </div>
    </div>
  );
  */

  // ── Step 4: Job Description ──

  const renderStep4 = () => (
    <div className="ajm-step-content">
      <div className="ajm-step-header">
        <h2 className="ajm-step-title-main">Job Description</h2>
        <p className="ajm-step-subtitle">Describe the role, responsibilities, and benefits</p>
      </div>

      {/* Description Editor */}
      <div className="ajm-field-group">
        <label className="ajm-section-label">Job Description <span className="ajm-required">*</span></label>
        <div className={`ajm-editor-container ${step4Errors.description ? 'ajm-editor-container--error' : ''}`}>
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={description}
            // WITH this
           // WITH this — Quill trailing \n hatao
onChange={(value, delta, source, editor) => {
  const currentLength = editor.getLength() - 1;
  if (currentLength <= 1000) {
    setDescription(value);
  } else {
    if (source === 'user') {
      const quill = quillRef.current?.getEditor();
      if (quill) {
        quill.deleteText(1000, currentLength);
      }
    }
  }
  setStep4Errors((prev) => ({ ...prev, description: '' }));
}}
onKeyDown={(e) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = description;
  const plainText = tempDiv.textContent || tempDiv.innerText || '';
  if (plainText.length >= 1000) {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Escape', 'Enter'];
    if (!allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
    }
  }
}}
            placeholder="Describe the role, responsibilities, and what success looks like in this position..."
            modules={{
              toolbar: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ header: [1, 2, 3, false] }],
                ['link'],
                ['clean']
              ],
            }}
          />
        </div>
        {step4Errors.description && <span className="ajm-error-text">{step4Errors.description}</span>}

        <div className="ajm-char-count">
          {(() => {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = description;
            const plainText = tempDiv.textContent || tempDiv.innerText || '';
            const count = plainText.length;
            const isOverLimit = count >= 1000;
            return (
              <span style={{ color: isOverLimit ? '#dc2626' : 'inherit', fontWeight: isOverLimit ? 600 : 'normal' }}>
                {count} / 1000 characters
              </span>
            );
          })()}
        </div>
      </div>

      {/* Responsibilities */}
      <div className="ajm-field-group">
        <label className="ajm-section-label">Key Responsibilities <span className="ajm-required">*</span></label>
        <div className="ajm-dynamic-list">
          {responsibilities.map((item, idx) => (
            <div 
              key={idx} 
              className="ajm-list-item"
              draggable
              onDragStart={() => setDragItemIndex({ list: 'responsibilities', index: idx })}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragItemIndex?.list === 'responsibilities' && dragItemIndex.index !== idx) {
                  const next = [...responsibilities];
                  const [removed] = next.splice(dragItemIndex.index, 1);
                  next.splice(idx, 0, removed);
                  setResponsibilities(next);
                }
                setDragItemIndex(null);
              }}
            >
              <GripVertical size={16} className="ajm-drag-handle" style={{ cursor: "grab" }} />
              <input
                type="text"
                value={item}
                placeholder={`Type or paste responsibilities. Press Enter or use ‘.’ to add a new line automatically.”`}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.endsWith('.') && val.trim().length > 1) {
                    const next = [...responsibilities];
                    next[idx] = val;
                    next.splice(idx + 1, 0, '');
                    setResponsibilities(next);
                    setTimeout(() => {
                      const inputs = document.querySelectorAll<HTMLInputElement>('.ajm-resp-input');
                      inputs[idx + 1]?.focus();
                    }, 0);
                  } else {
                    const next = [...responsibilities];
                    next[idx] = val;
                    setResponsibilities(next);
                  }
                  if (val.trim()) setStep4Errors(prev => ({ ...prev, responsibilities: '' }));
                }}
                onKeyDown={(e) => {
                  if (e.key === '.' || e.key === 'Enter') {
                    const val = (e.target as HTMLInputElement).value;
                    if (val.trim()) {
                      e.preventDefault();
                      const next = [...responsibilities];
                      next[idx] = val + (e.key === '.' ? '.' : '');
                      next.splice(idx + 1, 0, '');
                      setResponsibilities(next);
                      setTimeout(() => {
                        const inputs = document.querySelectorAll<HTMLInputElement>('.ajm-resp-input');
                        inputs[idx + 1]?.focus();
                      }, 0);
                    }
                  }
                }}
                className="ajm-list-input ajm-resp-input"
              />
              <button
                type="button"
                className="ajm-list-remove"
                onClick={() => {
                  const next = responsibilities.filter((_, i) => i !== idx);
                  setResponsibilities(next.length ? next : ['']);
                }}
              >
                <X size={14} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="ajm-add-item-btn"
            onClick={() => setResponsibilities([...responsibilities, ''])}
          >
            <Plus size={14} /> Add responsibility
          </button>
        </div>
        {step4Errors.responsibilities && <span className="ajm-error-text">{step4Errors.responsibilities}</span>}
      </div>

      {/* Requirements */}
      <div className="ajm-field-group">
        <label className="ajm-section-label">Requirements <span className="ajm-required">*</span></label>
        <div className="ajm-dynamic-list">
          {requirements.map((item, idx) => (
            <div 
              key={idx} 
              className="ajm-list-item"
              draggable
              onDragStart={() => setDragItemIndex({ list: 'requirements', index: idx })}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragItemIndex?.list === 'requirements' && dragItemIndex.index !== idx) {
                  const next = [...requirements];
                  const [removed] = next.splice(dragItemIndex.index, 1);
                  next.splice(idx, 0, removed);
                  setRequirements(next);
                }
                setDragItemIndex(null);
              }}
            >
              <GripVertical size={16} className="ajm-drag-handle" style={{ cursor: "grab" }} />
              <input
                type="text"
                value={item}
                placeholder={` Type or paste Requirement. Press Enter or use ‘.’ to add a new line automatically.”`}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.endsWith('.') && val.trim().length > 1) {
                    const next = [...requirements];
                    next[idx] = val;
                    next.splice(idx + 1, 0, '');
                    setRequirements(next);
                    setTimeout(() => {
                      const inputs = document.querySelectorAll<HTMLInputElement>('.ajm-req-input');
                      inputs[idx + 1]?.focus();
                    }, 0);
                  } else {
                    const next = [...requirements];
                    next[idx] = val;
                    setRequirements(next);
                  }
                  if (val.trim()) setStep4Errors(prev => ({ ...prev, requirements: '' }));
                }}
                onKeyDown={(e) => {
                  if (e.key === '.' || e.key === 'Enter') {
                    const val = (e.target as HTMLInputElement).value;
                    if (val.trim()) {
                      e.preventDefault();
                      const next = [...requirements];
                      next[idx] = val + (e.key === '.' ? '.' : '');
                      next.splice(idx + 1, 0, '');
                      setRequirements(next);
                      setTimeout(() => {
                        const inputs = document.querySelectorAll<HTMLInputElement>('.ajm-req-input');
                        inputs[idx + 1]?.focus();
                      }, 0);
                    }
                  }
                }}
                className="ajm-list-input ajm-req-input"
              />
              <button
                type="button"
                className="ajm-list-remove"
                onClick={() => {
                  const next = requirements.filter((_, i) => i !== idx);
                  setRequirements(next.length ? next : ['']);
                }}
              >
                <X size={14} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="ajm-add-item-btn"
            onClick={() => setRequirements([...requirements, ''])}
          >
            <Plus size={14} /> Add requirement
          </button>
        </div>
        {step4Errors.requirements && <span className="ajm-error-text">{step4Errors.requirements}</span>}
      </div>

      {/* Benefits */}
      <div className="ajm-field-group">
        <label className="ajm-section-label">Benefits & Perks</label>
        <div className="ajm-dynamic-list">
          {benefits.map((item, idx) => {
            const isStandard = hasMatchedText(perkSuggestions, item);
            if (isStandard) return null;
            return (
              <div 
                key={idx} 
                className="ajm-list-item"
                draggable
                onDragStart={() => setDragItemIndex({ list: 'benefits', index: idx })}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragItemIndex?.list === 'benefits' && dragItemIndex.index !== idx) {
                    const next = [...benefits];
                    const [removed] = next.splice(dragItemIndex.index, 1);
                    next.splice(idx, 0, removed);
                    setBenefits(next);
                  }
                  setDragItemIndex(null);
                }}
              >
                <GripVertical size={16} className="ajm-drag-handle" style={{ cursor: "grab" }} />
                <input
                  type="text"
                  value={item}
                  placeholder={`Type or paste Benefits & Perks. Press Enter or use '.' to add a new line automatically."`}
                  onChange={(e) => {
                    const val = e.target.value;
                    const target = e.target as HTMLElement;
                    if (val.endsWith('.') && val.trim().length > 1) {
                      const next = [...benefits];
                      next[idx] = val;
                      next.splice(idx + 1, 0, '');
                      setBenefits(next);
                      setTimeout(() => {
                        target.closest('.ajm-list-item')?.nextElementSibling?.querySelector('input')?.focus();
                      }, 0);
                    } else {
                      const next = [...benefits];
                      next[idx] = val;
                      setBenefits(next);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === '.' || e.key === 'Enter') {
                      const val = (e.target as HTMLInputElement).value;
                      const target = e.target as HTMLElement;
                      if (val.trim()) {
                        e.preventDefault();
                        const next = [...benefits];
                        next[idx] = val + (e.key === '.' ? '.' : '');
                        next.splice(idx + 1, 0, '');
                        setBenefits(next);
                        setTimeout(() => {
                          target.closest('.ajm-list-item')?.nextElementSibling?.querySelector('input')?.focus();
                        }, 0);
                      }
                    }
                  }}
                  className="ajm-list-input ajm-ben-input"
                />
                <button
                  type="button"
                  className="ajm-list-remove"
                  onClick={() => {
                    const next = benefits.filter((_, i) => i !== idx);
                    // Ensure at least one empty item if nothing custom is left
                    const hasCustom = next.some(n => !hasMatchedText(perkSuggestions, n));
                    setBenefits(hasCustom ? next : [...next, '']);
                  }}
                >
                  <X size={14} />
                </button>
              </div>
            );
          })}
          <button
            type="button"
            className="ajm-add-item-btn"
            onClick={() => setBenefits([...benefits, ''])}
          >
            <Plus size={14} /> Add benefit & Perks
          </button>
          {perkSuggestions.length > 0 && (
            <div className="ajm-perks-grid mt-2">
              {perkSuggestions.map((perk) => {
                const isSelected = hasMatchedText(benefits, perk);
                return (
                  <button
                    key={perk}
                    type="button"
                    className={`ajm-perk-btn ${isSelected ? 'ajm-perk-btn--active' : ''}`}
                    onClick={() => {
                      if (isSelected) {
                        setBenefits((prev) => {
                          const next = prev.filter((benefit) => !hasMatchedText([benefit], perk));
                          const hasCustom = next.some(n => !hasMatchedText(perkSuggestions, n));
                          return hasCustom ? next : [...next, ''];
                        });
                        return;
                      }
                      setBenefits((prev) => {
                        const next = [...prev];
                        // If the only thing in prev was a single empty string, we can keep it
                        // so the user still sees a custom input, or we can just append the perk.
                        // Let's just append the perk.
                        return hasMatchedText(next, perk) ? next : [...next, perk];
                      });
                    }}
                  >
                    {isSelected ? <Check size={12} /> : <Plus size={12} />}
                    {perk}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Job Status Toggle (for edit mode) */}
      {/* {jobDetails?.id && ( */}
      {/* // <div className={`ajm-field-group ajm-field-group--highlight ${isClosed ? 'ajm-field-group--closed' : ''}`}> */}
      {/* <ToggleSwitch
            checked={isClosed}
            onChange={(val) => val ? setShowCloseConfirm(true) : setIsClosed(false)}
            label="Close this job posting"
            description="Stop receiving new applications for this position"
            variant="danger"
          /> */}
      {/* </div> */}
      {/* )} */}
    </div>
  );

  // ── Step 5: Communication Preferences ──

  const renderStep5 = () => (
    <div className="ajm-step-content">
      <div className="ajm-step-header">
        <h2 className="ajm-step-title-main">Communication Preferences</h2>
        <p className="ajm-step-subtitle">Choose how you want to be notified about new applications</p>
      </div>

      <div className="ajm-info-banner">
        <div className="ajm-info-icon">🚀</div>
        <div className="ajm-info-content">
          <div className="ajm-info-title">Your job will be live after review</div>
          <div className="ajm-info-text">Usually takes less than 30 minutes during business hours</div>
        </div>
      </div>

      <div className="ajm-communication-options">
        <ToggleSwitch
          checked={notifyEmail}
          onChange={setNotifyEmail}
          label="Email Notifications"
          description="Receive detailed applicant information via email"
        />
        {/* <ToggleSwitch
          checked={notifySms}
          onChange={setNotifySms}
          label="SMS Alerts"
          description="Get instant text alerts for new applications"
        />
        <ToggleSwitch
          checked={notifyWhatsapp}
          onChange={setNotifyWhatsapp}
          label="WhatsApp Notifications"
          description="Receive summaries and updates on WhatsApp"
        /> */}
      </div>

      {/* Multiple Email Recipients */}
      {/* <div className="ajm-field-group">
        <label className="ajm-section-label">Notification Email Recipients <span className="ajm-optional">(Optional)</span></label>
        <p className="ajm-section-hint">Add multiple emails — e.g. HR + hiring manager</p>
        <div className="ajm-dynamic-list">
          {notifyEmails.map((email, idx) => (
            <div key={idx} className="ajm-list-item">
              <input
                type="email"
                value={email}
                placeholder="email@company.com"
                onChange={(e) => {
                  const next = [...notifyEmails];
                  next[idx] = e.target.value;
                  setNotifyEmails(next);
                }}
                className="ajm-list-input"
              />
              <button type="button" className="ajm-list-remove" onClick={() => {
                const next = notifyEmails.filter((_, i) => i !== idx);
                setNotifyEmails(next.length ? next : ['']);
              }}><X size={14} /></button>
            </div>
          ))}
          <button type="button" className="ajm-add-item-btn" onClick={() => setNotifyEmails([...notifyEmails, ''])}>
            <Plus size={14} /> Add email
          </button>
        </div>
      </div> */}

      {/* Applicant Acknowledgement Template */}
      {/* <div className="ajm-field-group">
        <label className="ajm-section-label">Applicant Acknowledgement Template <span className="ajm-optional">(Optional)</span></label>
        <p className="ajm-section-hint">Customize the auto-reply sent to applicants after they apply</p>
        <textarea
          value={ackTemplate}
          onChange={(e) => setAckTemplate(e.target.value)}
          placeholder="Thank you for applying to [Job Title] at [Company]. We'll review your application and get back to you within 5–7 business days..."
          className="ajm-textarea"
          rows={4}
        />
      </div> */}

      <div className="ajm-preview-section">
        <div className="ajm-preview-header">
          <div className="ajm-preview-title">Job Posting Preview</div>
        </div>
        <div className="ajm-preview-summary">
          <div className="ajm-preview-item">
            <span className="ajm-preview-label">Position:</span>
            <span className="ajm-preview-value">{jobTitle || 'Not specified'}</span>
          </div>
          {department && (
            <div className="ajm-preview-item">
              <span className="ajm-preview-label">Department:</span>
              <span className="ajm-preview-value">{department}</span>
            </div>
          )}
          <div className="ajm-preview-item">
            <span className="ajm-preview-label">Experience:</span>
            <span className="ajm-preview-value">{selectedExperienceLabel || 'Not specified'}</span>
          </div>
          <div className="ajm-preview-item">
            <span className="ajm-preview-label">Location:</span>
            <span className="ajm-preview-value">{locationText || 'Not specified'}</span>
          </div>
          {employmentTypeIds.length > 0 && (
            <div className="ajm-preview-item">
              <span className="ajm-preview-label">Employment Type:</span>
              <span className="ajm-preview-value">
                {employmentTypeOptions.filter(o => employmentTypeIds.includes(o.value)).map(o => o.label).join(', ')}
              </span>
            </div>
          )}
          {workMode && (
            <div className="ajm-preview-item">
              <span className="ajm-preview-label">Work Mode:</span>
              <span className="ajm-preview-value">{selectedWorkModeLabel}</span>
            </div>
          )}
          {applicationDeadline && (
            <div className="ajm-preview-item">
              <span className="ajm-preview-label">Deadline:</span>
              <span className="ajm-preview-value">{new Date(applicationDeadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </div>
          )}
          {(salaryVisibility !== 'range' || salaryMin !== '' || salaryMax !== '') && (
            <div className="ajm-preview-item">
              <span className="ajm-preview-label">Salary:</span>
              <span className="ajm-preview-value">
                {salaryVisibility !== 'range' && selectedSalaryVisibilityLabel}
                {salaryVisibility === 'range' && `${salaryCurrency} ${salaryMin !== '' ? Number(salaryMin).toLocaleString() : '—'} – ${salaryMax !== '' ? Number(salaryMax).toLocaleString() : '—'} / ${salaryPeriodOptions.find(p => p.value === salaryPeriod)?.label || salaryPeriod}`}
              </span>
            </div>
          )}
          {openingsCount !== '' && (
            <div className="ajm-preview-item">
              <span className="ajm-preview-label">Openings:</span>
              <span className="ajm-preview-value">{openingsCount}</span>
            </div>
          )}
          <div className="ajm-preview-item">
            <span className="ajm-preview-label">Skills:</span>
            <span className="ajm-preview-value">
              {selectedSkills.length ? selectedSkills.map(s => s.name).join(', ') : 'Not specified'}
            </span>
          </div>
          {qualification.length > 0 && (
            <div className="ajm-preview-item">
              <span className="ajm-preview-label">Qualification:</span>
              <span className="ajm-preview-value">{qualification.join(', ')}</span>
            </div>
          )}
          {industryTags.length > 0 && (
            <div className="ajm-preview-item">
              <span className="ajm-preview-label">Industry:</span>
              <span className="ajm-preview-value">{industryTags.join(', ')}</span>
            </div>
          )}
          <div className="ajm-preview-item">
            <span className="ajm-preview-label">Gender Preference:</span>
            <span className="ajm-preview-value">
              {genderOptions.find(g => g.value?.toLowerCase() === (genderPreference || '').toLowerCase())?.label || genderPreference || 'Any'}
            </span>
          </div>
          <div className="ajm-preview-item">
            <span className="ajm-preview-label">Language Preference:</span>
            <span className="ajm-preview-value">
              {(Array.isArray(languageRequirements) ? languageRequirements : [languageRequirements]).filter(Boolean).length > 0
                ? (Array.isArray(languageRequirements) ? languageRequirements : [languageRequirements]).filter(Boolean).join(', ')
                : 'Not specified'}
            </span>
          </div>
          {getRichTextPlainText(description).trim() && (
            <div className="ajm-preview-item ajm-preview-item--block">
              <span className="ajm-preview-label">Description:</span>
              <div
                className="ajm-preview-description ql-editor"
                dangerouslySetInnerHTML={{ __html: sanitizeRichTextPreview(description) }}
              />
            </div>
          )}
          {responsibilities.filter(Boolean).length > 0 && (
            <div className="ajm-preview-item ajm-preview-item--block">
              <span className="ajm-preview-label">Responsibilities:</span>
              <ul className="ajm-preview-list">
                {responsibilities.filter(Boolean).map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
          {requirements.filter(Boolean).length > 0 && (
            <div className="ajm-preview-item ajm-preview-item--block">
              <span className="ajm-preview-label">Requirements:</span>
              <ul className="ajm-preview-list">
                {requirements.filter(Boolean).map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
          {benefits.filter(Boolean).length > 0 && (
            <div className="ajm-preview-item ajm-preview-item--block">
              <span className="ajm-preview-label">Benefits:</span>
              <ul className="ajm-preview-list">
                {benefits.filter(Boolean).map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          )}
          {aboutCompany?.trim() && (
            <div className="ajm-preview-item ajm-preview-item--block">
              <span className="ajm-preview-label">About Company:</span>
              <span className="ajm-preview-value">{aboutCompany}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ── Render ──

  const renderSkeleton = () => (
    <>
      {/* Skeleton step indicator */}
      <div className="ajm-step-indicator">
        <div className="ajm-step-progress-bar">
          <div className="ajm-step-progress-fill" style={{ width: '0%' }} />
        </div>
        <div className="ajm-step-list">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="ajm-step-item">
              <div className="ajm-step-bubble">
                <SkeletonLoader width={14} height={14} circle />
              </div>
              <div className="ajm-step-info">
                <SkeletonLoader width={80} height={12} style={{ marginBottom: 4 }} />
                <SkeletonLoader width={60} height={10} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Skeleton body */}
      <div className="ajm-modal-body">
        <div className="ajm-step-content">
          {/* Section header */}
          <div className="ajm-step-header">
            <SkeletonLoader width={160} height={24} style={{ marginBottom: 8 }} />
            <SkeletonLoader width={280} height={16} />
          </div>

          {/* Card 1 */}
          <div className="ajm-section-card">
            <div className="ajm-section-card__header">
              <SkeletonLoader width={120} height={18} style={{ marginBottom: 6 }} />
              <SkeletonLoader width={200} height={13} />
            </div>
            <div className="ajm-inline-grid">
              <div className="ajm-field-group">
                <SkeletonLoader width={80} height={13} style={{ marginBottom: 6 }} />
                <SkeletonLoader width="100%" height={42} style={{ borderRadius: 10 }} />
              </div>
              <div className="ajm-field-group">
                <SkeletonLoader width={80} height={13} style={{ marginBottom: 6 }} />
                <SkeletonLoader width="100%" height={42} style={{ borderRadius: 10 }} />
              </div>
            </div>
            <div className="ajm-inline-grid">
              <div className="ajm-field-group">
                <SkeletonLoader width={100} height={13} style={{ marginBottom: 6 }} />
                <SkeletonLoader width="100%" height={42} style={{ borderRadius: 10 }} />
              </div>
              <div className="ajm-field-group">
                <SkeletonLoader width={100} height={13} style={{ marginBottom: 6 }} />
                <SkeletonLoader width="100%" height={42} style={{ borderRadius: 10 }} />
              </div>
            </div>
          </div>

          {/* Card 2 */}
          <div className="ajm-section-card">
            <div className="ajm-section-card__header">
              <SkeletonLoader width={140} height={18} style={{ marginBottom: 6 }} />
              <SkeletonLoader width={220} height={13} />
            </div>
            <div className="ajm-field-group">
              <SkeletonLoader width={110} height={13} style={{ marginBottom: 6 }} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <SkeletonLoader key={i} width={100} height={36} style={{ borderRadius: 999 }} />
                ))}
              </div>
            </div>
            <div className="ajm-field-group">
              <SkeletonLoader width={90} height={13} style={{ marginBottom: 6 }} />
              <SkeletonLoader width="100%" height={42} style={{ borderRadius: 10 }} />
            </div>
          </div>

          {/* Card 3 */}
          <div className="ajm-section-card">
            <div className="ajm-section-card__header">
              <SkeletonLoader width={100} height={18} style={{ marginBottom: 6 }} />
              <SkeletonLoader width={180} height={13} />
            </div>
            <div className="ajm-inline-grid">
              <div className="ajm-field-group">
                <SkeletonLoader width={70} height={13} style={{ marginBottom: 6 }} />
                <SkeletonLoader width="100%" height={42} style={{ borderRadius: 10 }} />
              </div>
              <div className="ajm-field-group">
                <SkeletonLoader width={70} height={13} style={{ marginBottom: 6 }} />
                <SkeletonLoader width="100%" height={42} style={{ borderRadius: 10 }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Skeleton footer */}
      <div className="ajm-modal-footer">
        <SkeletonLoader width={80} height={14} />
        <div style={{ display: 'flex', gap: 10 }}>
          <SkeletonLoader width={90} height={40} style={{ borderRadius: 10 }} />
          <SkeletonLoader width={120} height={40} style={{ borderRadius: 10 }} />
        </div>
      </div>
    </>
  );

  return (
    <div className="ajm-page">
      <div className="ajm-page-container">
        {/* Header */}
        <div className="ajm-modal-header">
          <div className="ajm-header-content">
            <button
              type="button"
              className="ajm-back-to-jobs-btn"
              onClick={handleClose}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                background: '#ffffff',
                color: '#475569',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                marginRight: 8,
                transition: 'all 0.15s ease',
              }}
            >
              <ChevronLeft size={16} />
              Back to Jobs
            </button>
            <div className="ajm-header-icon">
              <Briefcase size={20} />
            </div>
            <div className="ajm-header-text">
              <h1 className="ajm-header-title">
                {jobDetails?.id ? 'Edit Job Opening' : 'Post a New Job'}
              </h1>
              <p className="ajm-header-subtitle">
                Step {activeStep + 1} of {STEPS.length}: {STEPS?.[activeStep]?.label || ''}
              </p>
            </div>
          </div>
          <div className='d-flex gap-2 align-items-center'>
            {jobDetails?.id && (
              <div className={`ajm-header-close-job ${isClosed ? 'ajm-header-close-job--closed' : ''}`}>
                <span className="ajm-header-close-job-label">Close Job</span>
                <button
                  type="button"
                  className={`ajm-toggle-switch ${isClosed ? 'ajm-toggle-switch--on ajm-toggle-switch--danger' : ''}`}
                  onClick={() => {
                    if (isClosed) {
                      // Reopening: check if application deadline is in the past
                      if (applicationDeadline) {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const deadline = new Date(applicationDeadline);
                        if (deadline < today) {
                          setReopenDeadlineError(true);
                          setStep1Errors(prev => ({
                            ...prev,
                            applicationDeadline: 'Application deadline has passed. Please update or clear the deadline before reopening this job.',
                          }));
                          if (activeStep !== 0) setActiveStep(0 as StepIndex);
                          return;
                        }
                      }
                      setReopenDeadlineError(false);
                      setIsClosed(false);
                    } else {
                      setShowCloseConfirm(true);
                    }
                  }}
                  title={isClosed ? 'Reopen job' : 'Close job posting'}
                >
                  <span className="ajm-toggle-thumb">
                  </span>
                </button>
              </div>
            )}
            <button type="button" className="ajm-close-btn" onClick={handleClose}>
              <X size={20} />
            </button>
          </div>

        </div>

        {/* Reopen deadline error banner — shown directly below header */}
        {reopenDeadlineError && (
          <div style={{
            background: "#FEF2F2",
            border: "1px solid #FECACA",
            borderRadius: 10,
            padding: "10px 16px",
            margin: "0 0 12px 0",
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            fontSize: 13,
            color: "#DC2626",
          }}>
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              <strong>Cannot reopen job:</strong> The application deadline ({applicationDeadline}) has already passed.
              Please <strong>update or clear the deadline</strong> in Job Basics (Step 1) before reopening.
            </span>
          </div>
        )}

        {/* Progress Indicator + Body: show skeleton while form options load */}
        {isLoadingFormOptions ? renderSkeleton() : (
          <>
            {/* Progress Indicator */}
            {renderStepIndicator()}

            {/* Main Content */}
            <div className="ajm-modal-body" ref={modalBodyRef}>
              <div className={`ajm-step-wrapper ajm-step-wrapper--${direction}`}>
                {activeStep === 0 && renderStep1()}
                {activeStep === 1 && renderStep2()}
                {activeStep === 2 && renderStep4()}
                {activeStep === 3 && renderStep5()}
              </div>
            </div>
          </>
        )}

        {/* Footer Actions */}
        <div className="ajm-modal-footer">
          <div className="ajm-footer-actions">
            {activeStep > 0 ? (
              <button
                type="button"
                className="ajm-btn ajm-btn--secondary"
                disabled={isNavigating}
                onClick={goBack}
              >
                <ChevronLeft size={16} />
                Back
              </button>
            ) : (
              <button
                type="button"
                className="ajm-btn ajm-btn--ghost"
                onClick={handleClose}
              >
                <ChevronLeft size={16} style={{ marginRight: 4 }} />
                Back to Jobs
              </button>
            )}

            <button
              type="button"
              className="ajm-btn ajm-btn--draft"
              disabled={isSavingDraft || isSubmittingBtn || isNavigating}
              onClick={handleSaveAndClose}
            >
              {isSavingDraft ? (
                <>
                  <SkeletonLoader inline width={14} height={14} style={{ borderRadius: 4, marginRight: 6 }} />
                  Saving...
                </>
              ) : (
                'Save as Draft'
              )}
            </button>

            {activeStep < 3 ? (
              <button
                type="button"
                className="ajm-btn ajm-btn--primary"
                disabled={isNavigating || isSavingDraft || isSubmittingBtn}
                onClick={()=>goNext()}
              >
                {isNavigating && !isSavingDraft ? (
                  <>
                    <SkeletonLoader inline width={14} height={14} style={{ borderRadius: 4, marginRight: 6 }} />
                    {isEditingExistingJob ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  <>{isEditingExistingJob ? 'Update & Continue' : 'Save & Continue'}<ChevronRight size={16} /></>
                )}
              </button>
            ) : (
                 <button
                      type="button"
                      className="ajm-btn ajm-btn--primary ajm-btn--lg"
                      disabled={isSubmittingBtn || isSavingDraft || isNavigating}
                      onClick={handleFinalSubmit}
                    >
                      {isSubmittingBtn ? (
                        <>
                          <SkeletonLoader inline width={16} height={16} style={{ borderRadius: 4, marginRight: 8 }} />
                          {isClosed ? 'Updating...' : 'Publishing...'}
                        </>
                      ) : (
                        <>
                          {isClosed ? 'Update Job' : 'Publish Job'}
                          <Check size={16} style={{ marginLeft: 4 }} />
                        </>
                      )}
                    </button>
              
            )}
          </div>

          {/* <div className="ajm-footer-hint">
            {activeStep < 3 ? 'Or press Enter to continue' : 'Review your details before posting'}
          </div> */}
        </div>
      </div>

      {/* ── Leave Confirm Dialog ── */}
      {showLeaveConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,20,40,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: '32px 28px 28px', maxWidth: 420, width: '100%', boxShadow: '0 24px 60px rgba(10,20,40,0.18)', textAlign: 'center', position: 'relative' }}>
            <button
              onClick={handleLeaveWithoutSaving}
              style={{ position: 'absolute', top: 14, right: 14, width: 32, height: 32, borderRadius: '50%', border: '1px solid #e5e7eb', background: '#f8f9fc', color: '#606b7b', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
            >
              ×
            </button>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: '#FFF8E8', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 26 }}>
              📝
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#102144', margin: '0 0 8px' }}>Save as Draft?</h3>
            <p style={{ fontSize: 14, color: '#71809d', margin: '0 0 24px', lineHeight: 1.6 }}>
              You have unsaved changes for <strong>"{jobTitle}"</strong>. Would you like to save it as a draft so you can continue later?
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button
                onClick={handleLeaveWithoutSaving}
                style={{ height: 48, borderRadius: 12, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
              >
                Discard
              </button>
              {/* <button
                onClick={handleLeaveAndDraft}
                disabled={isSavingDraft}
                style={{ height: 48, borderRadius: 12, border: 'none', background: '#002781', color: '#fff', fontSize: 14, fontWeight: 600, cursor: isSavingDraft ? 'not-allowed' : 'pointer', opacity: isSavingDraft ? 0.8 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                {isSavingDraft ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'ajm-spin 0.8s linear infinite' }}>
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                    Saving...
                  </>
                ) : 'Save Draft'}
              </button> */}
            </div>
          </div>
        </div>
      )}


      {showCloseConfirm && (
        <div className="qa-overlay" onClick={() => setShowCloseConfirm(false)}>
          <div className="qa-modal qa-modal--sm" onClick={e => e.stopPropagation()}>
            <button className="qa-close-btn qa-close-btn--abs" onClick={() => setShowCloseConfirm(false)}>
              <X size={18} />
            </button>
            <div className="qa-modal-icon qa-modal-icon--red">
              <AlertTriangle size={24} />
            </div>
            <h2 className="qa-modal-title qa-modal-title--center">Close Position</h2>
            <p className="qa-modal-sub qa-modal-sub--center">
              Are you sure you want to close <strong>"{jobDetails?.job_title || jobDetails?.title}"</strong>? This will stop accepting new applications.
            </p>
            <div className="qa-warn-box">
              <div className="qa-warn-box-title"><AlertTriangle size={14} /> ACTIVE CANDIDATES WILL BE AFFECTED</div>
              <div className="qa-warn-box-row"><Clock size={14} color="#d68600" /><span>Applicants will no longer be able to apply once this position is closed.</span></div>
              <div className="qa-warn-box-row"><Users size={14} color="#2c63d6" /><span>Existing candidate records and interview progress will remain available.</span></div>
            </div>
            <div className="qa-modal-actions">
              <button className="qa-btn qa-btn--ghost" onClick={() => setShowCloseConfirm(false)}>Keep Open</button>
              <button
                className="qa-btn qa-btn--danger"
                disabled={isClosingJob}
                onClick={async () => {
                  const jobId = jobDetails?.id ?? persistedJobId;
                  if (!jobId) {
                    // No job saved yet — just mark closed locally and close modal
                    setIsClosed(true);
                    setShowCloseConfirm(false);
                    onCancel();
                    return;
                  }
                  setIsClosingJob(true);
                  try {
                    const res = await apiService.toggleJobStatus(Number(jobId), { action: 'close' });
                    if (res?.status) {
                      toast.success('Position closed successfully');
                      setShowCloseConfirm(false);
                      onCancel();
                    } else {
                      toast.error(res?.message || 'Failed to close position');
                    }
                  } catch {
                    toast.error('Failed to close position');
                  } finally {
                    setIsClosingJob(false);
                  }
                }}
              >
                {isClosingJob ? (
                  <>
                    <SkeletonLoader inline width={14} height={14} style={{ borderRadius: 4, marginRight: 6 }} />
                    Closing...
                  </>
                ) : (
                  <><XCircle size={16} /> Close Position</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Verification Required Modal */}
      <VerificationRequiredModal
        show={showVerificationModal}
        onHide={() => {
          setShowVerificationModal(false);
          onCancel();
        }}
        title="Domain Verification Required"
        message="Your job position has been safely saved as a draft. Please verify your company domain to publish this job and start receiving candidates."
      />

      {/* Credit Confirmation Modal */}
      {isCreditPopupOpen && creditPopupOptions && (
        <CreditConfirmPopup
          isOpen={isCreditPopupOpen}
          {...creditPopupOptions}
          onConfirm={handleCreditConfirm}
          onCancel={handleCreditCancel}
          onNeverShowAgain={handleCreditNeverShowAgain}
        />
      )}
    </div>
  );
};
// ─── react-select styles ──────────────────────────────────────────────────────

function selectStyles(hasError: boolean) {
  return {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: 48,
      borderRadius: 12,
      borderColor: hasError ? '#E24B4A' : state.isFocused ? '#185FA5' : '#E5E7EB',
      borderWidth: 1.5,
      boxShadow: state.isFocused && !hasError ? '0 0 0 3px rgba(24, 95, 165, 0.1)' : 'none',
      background: '#FAFBFC',
      '&:hover': { borderColor: hasError ? '#E24B4A' : '#185FA5' },
      fontSize: 14,
      transition: 'all 0.2s ease',
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: '#E6F1FB',
      borderRadius: 8,
      padding: '2px 6px',
      fontSize: 13,
    }),
    multiValueLabel: (base: any) => ({ ...base, color: '#185FA5', fontWeight: 500 }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: '#185FA5',
      borderRadius: 6,
      '&:hover': { backgroundColor: '#185FA5', color: '#fff' },
    }),
    placeholder: (base: any) => ({ ...base, fontSize: 14, color: '#9CA3AF' }),
    menu: (base: any) => ({ ...base, borderRadius: 12, fontSize: 14, marginTop: 4 }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected ? '#E6F1FB' : state.isFocused ? '#F3F4F6' : 'transparent',
      color: state.isSelected ? '#185FA5' : '#374151',
      fontWeight: state.isSelected ? 500 : 400,
    }),
  };
}

export default AddJobModal;
